import React, { useEffect, useMemo, useRef, useState } from "react";
import { read, utils } from "xlsx";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import VersionHistory from "./VersionHistory";
import SectionFilter from "./SectionFilter";
import { matchesSection } from "../utils/sections";
import { scanContractorSubmission } from "../utils/scanCertificate";
import { openHtmlWindow } from "../utils/printDoc";

// ─── Calculation tooltip ───────────────────────────────────────────────────────
// Click the ⓘ icon to open a pinned popover. Click anywhere outside to close.
function CalcTooltip({ lines = [], title = "" }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, alignRight: false });
  const iconRef = useRef(null);
  const popRef = useRef(null);

  const toggle = (e) => {
    e.stopPropagation();
    if (open) { setOpen(false); return; }
    setOpen(true);
  };

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (popRef.current && !popRef.current.contains(e.target) &&
          iconRef.current && !iconRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      <span
        ref={iconRef}
        onClick={toggle}
        style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          width: 16, height: 16, borderRadius: "50%",
          background: open ? "#0369a1" : "#e0f2fe",
          color: open ? "#fff" : "#0369a1",
          fontSize: 10, fontWeight: 800, cursor: "pointer",
          flexShrink: 0, userSelect: "none", lineHeight: 1,
          transition: "background 0.15s, color 0.15s",
        }}
      >i</span>

      {open && (
        <>
          <div
            style={{ position: "fixed", inset: 0, zIndex: 9998, background: "rgba(15,23,42,0.25)" }}
            onClick={() => setOpen(false)}
          />
          <div
            ref={popRef}
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 9999,
              background: "#fff",
              border: "1px solid #cbd5e1",
              borderRadius: 12,
              boxShadow: "0 24px 60px rgba(15,23,42,0.28)",
              padding: "14px 18px",
              width: 420,
              maxWidth: "calc(100vw - 32px)",
              maxHeight: "80vh",
              overflowY: "auto",
              fontSize: 12,
              color: "#0f172a",
            }}
            onClick={(e) => e.stopPropagation()}
          >
          {/* Header */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div style={{ fontWeight: 800, fontSize: 12, color: "#0f172a", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              {title}
            </div>
            <span
              onClick={() => setOpen(false)}
              style={{ cursor: "pointer", color: "#94a3b8", fontSize: 14, lineHeight: 1, padding: "0 2px" }}
            >✕</span>
          </div>

          {/* Breakdown table */}
          <table style={{ borderCollapse: "collapse", width: "100%" }}>
            <tbody>
              {lines.map((line, i) => {
                if (line.separator) {
                  return (
                    <tr key={i}>
                      <td colSpan={2} style={{ borderTop: "1px solid #e2e8f0", padding: "3px 0" }} />
                    </tr>
                  );
                }
                return (
                  <tr key={i} style={{ background: line.highlight ? "#f8fbff" : "transparent" }}>
                    <td style={{
                      padding: "4px 10px 4px 0",
                      paddingLeft: line.indent ? 16 : 0,
                      color: line.highlight ? "#0f172a" : "#64748b",
                      fontWeight: line.highlight ? 700 : 500,
                      whiteSpace: "nowrap",
                      fontSize: line.highlight ? 12 : 11,
                      borderLeft: line.indent ? "2px solid #e2e8f0" : "none",
                      marginLeft: line.indent ? 8 : 0,
                    }}>
                      {line.label}
                    </td>
                    <td style={{
                      padding: "4px 0 4px 16px",
                      textAlign: "right",
                      fontWeight: line.highlight ? 700 : 500,
                      color: line.negative ? "#dc2626" : line.highlight ? "#0f172a" : "#374151",
                      whiteSpace: "nowrap",
                      fontSize: line.highlight ? 12 : 11,
                    }}>
                      {line.value}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </>
      )}
    </span>
  );
}



const numberOrZero = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  const cleaned = String(value).replace(/[^0-9.-]/g, "");
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
};

const roundToCertificate = (value) => Math.round(numberOrZero(value) * 100) / 100;

// display2dp: only used at the point of display, never in intermediate calculations
const display2dp = (value) => Math.round(numberOrZero(value) * 100) / 100;

const currency = (value) =>
  new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: "ZAR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numberOrZero(value));

const normalizeItemNo = (value) => String(value || "").trim().toLowerCase();

const compareCertificateRows = (a, b) => {
  const dateCompare = String(a.certificateDate || "").localeCompare(String(b.certificateDate || ""));
  if (dateCompare !== 0) return dateCompare;
  return String(a.certificateNo || a.id || "").localeCompare(String(b.certificateNo || b.id || ""), undefined, { numeric: true, sensitivity: "base" });
};

const getFirstPositiveNumber = (...values) => {
  for (const value of values) {
    const parsed = numberOrZero(value);
    if (parsed > 0) return parsed;
  }
  return 0;
};

// Subtotals: sum without rounding — rounding only happens at display
const buildClaimedItemsSubtotal = (items = []) =>
  items.reduce((sum, item) => {
    const amount = item.amountThisMonth !== undefined && item.amountThisMonth !== null && String(item.amountThisMonth) !== ""
      ? numberOrZero(item.amountThisMonth)
      : numberOrZero(item.thisMonthQty) * numberOrZero(item.rate);
    return sum + amount;
  }, 0);

// This-certificate value of scheduled items flagged "subject to CPA" (default on).
// Feeds the cumulative escalatable base that populates the CPA "Total scheduled items".
const buildCpaSubjectSubtotal = (items = []) =>
  (items || [])
    .filter((item) => item.subjectToCpa !== false)
    .reduce((sum, item) => {
      const amount = item.amountThisMonth !== undefined && item.amountThisMonth !== null && String(item.amountThisMonth) !== ""
        ? numberOrZero(item.amountThisMonth)
        : numberOrZero(item.thisMonthQty) * numberOrZero(item.rate);
      return sum + amount;
    }, 0);

const getVoCertifiedAmountThisCertificate = (item = {}) => {
  const preferredFields = [
    item.paymentCertificateVoAmount,
    item.amountCertifiedThisPeriod,
    item.certifiedAmountThisCertificate,
    item.certifiedAmount,
    item.paymentAmountThisCertificate,
  ];

  for (const value of preferredFields) {
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return numberOrZero(value);
    }
  }

  if (
    item.amountThisCertificate !== undefined &&
    item.amountThisCertificate !== null &&
    String(item.amountThisCertificate).trim() !== ""
  ) {
    return numberOrZero(item.amountThisCertificate);
  }

  return numberOrZero(item.quantityThisCertificate) * numberOrZero(item.rate);
};

const buildExtraWorkSubtotal = (items = []) =>
  items.reduce((sum, item) => sum + getVoCertifiedAmountThisCertificate(item), 0);

// P&G claims certified this certificate
const buildClaimsSubtotal = (items = []) =>
  items.reduce((sum, item) => sum + numberOrZero(item.amountThisCertificate), 0);

const getVariationApprovedValue = (variation = {}) =>
  numberOrZero(variation.approvedValue ?? variation.netVoAmount ?? variation.assessedValue ?? variation.quotedValue ?? variation.estimatedValue ?? variation.value ?? 0);

const getVariationApprovedQty = (variation = {}) => {
  const qty = numberOrZero(variation.approvedQty ?? variation.approvedQuantity ?? variation.assessedQty ?? variation.quantity ?? variation.qty ?? 1);
  return qty > 0 ? qty : 1;
};

const getVariationRate = (variation = {}, approvedValue = 0, approvedQty = 1) => {
  const explicitRate = numberOrZero(variation.rate ?? variation.approvedRate ?? variation.tenderRate ?? 0);
  if (explicitRate > 0) return explicitRate;
  return approvedQty > 0 ? approvedValue / approvedQty : approvedValue;
};

// Certificate forms keep a snapshot of each selected VO so issued certificates
// remain historically accurate. While a certificate is being prepared or
// edited, however, the selected rows must follow the current approved VO value.
// Preserve the entered quantity and refresh every derived monetary field.
const syncSelectedVariationItems = (items = [], approvedVariationRows = []) => {
  const sourceItems = Array.isArray(items) ? items : [];
  const rowsById = new Map();
  const rowsByNo = new Map();

  approvedVariationRows.forEach((row) => {
    const id = String(row.variationId || "").trim();
    const no = String(row.variationNo || "").trim();
    if (id) rowsById.set(id, row);
    if (no) rowsByNo.set(no, row);
  });

  let changed = false;
  const synced = sourceItems.flatMap((item) => {
    const id = String(item.variationId || "").trim();
    const no = String(item.variationNo || "").trim();
    const row = (id && rowsById.get(id)) || (no && rowsByNo.get(no));

    if (!row) {
      changed = true;
      return [];
    }

    const approvedValue = numberOrZero(row.approvedValue);
    const approvedQty = numberOrZero(row.approvedQty);
    const rate = numberOrZero(row.rate);
    const previouslyCertifiedQty = numberOrZero(row.previouslyCertifiedQty);
    const quantityThisCertificate = item.quantityThisCertificate ?? "";
    const cumulativeQty = previouslyCertifiedQty + numberOrZero(quantityThisCertificate);
    const amountThisCertificate = numberOrZero(quantityThisCertificate) * rate;
    const nextItem = {
      ...item,
      variationId: row.variationId,
      variationNo: row.variationNo,
      title: row.title,
      description: row.description,
      approvedValue,
      approvedQty,
      rate,
      previouslyCertifiedQty,
      quantityThisCertificate,
      cumulativeQty,
      amountThisCertificate,
      paymentCertificateVoAmount: amountThisCertificate,
      balanceValue: approvedValue - cumulativeQty * rate,
    };

    const isUnchanged = Object.keys(nextItem).every((key) => Object.is(nextItem[key], item[key]));
    if (isUnchanged) return [item];
    changed = true;
    return [nextItem];
  });

  return !changed && synced.length === sourceItems.length ? sourceItems : synced;
};

const getRetentionPercentage = (row = {}) => {
  if (row.retentionPercentage === undefined || row.retentionPercentage === null || String(row.retentionPercentage) === "") return 0.05;
  const value = numberOrZero(row.retentionPercentage);
  return value > 1 ? value / 100 : value;
};

const hasExplicitCertificateCpaLock = (row = {}) =>
  row.isCpaLocked === true ||
  (row.lockedCpaCumulative !== undefined && row.lockedCpaCumulative !== null && String(row.lockedCpaCumulative) !== "") ||
  (row.lockedCpaThisCertificate !== undefined && row.lockedCpaThisCertificate !== null && String(row.lockedCpaThisCertificate) !== "");

const getCertificateCpaLockLabel = (row = {}, fallbackStatus = "") => {
  if (!hasExplicitCertificateCpaLock(row)) return "Unlocked";
  const status = String(row.lockedCpaIndexStatus || fallbackStatus || "P").toUpperCase();
  return `Locked (${status || "P"})`;
};

const getCertificateInitialCpaCumulative = (row = {}) => {
  if (row.lockedCpaCumulative !== undefined && row.lockedCpaCumulative !== null && String(row.lockedCpaCumulative) !== "") return numberOrZero(row.lockedCpaCumulative);
  if (row.cpaCumulative !== undefined && row.cpaCumulative !== null && String(row.cpaCumulative) !== "") return numberOrZero(row.cpaCumulative);
  return 0;
};

const getCertificateCurrentCpaCumulative = (row = {}) => numberOrZero(row.currentCpaCumulative ?? row.liveCpaCumulative ?? 0);
const getCertificateCpaCumulative = (row = {}) => getCertificateCurrentCpaCumulative(row);

const getCertificateSubtotalB = (row = {}) => {
  // Always rebuild Subtotal B from the current displayed Subtotal A plus the live/current CPA cumulative.
  // Do not prioritise row.effectiveSubtotalB here, because it can become stale when Subtotal A is
  // corrected from stored certificate values or when CPA cumulative is refreshed from the CPA register.
  const subtotalA = numberOrZero(row.effectiveSubtotalA ?? row.subtotalA);
  return subtotalA + getCertificateCpaCumulative(row);
};

const getCertificateInitialCpaThisCertificate = (row = {}, previousInitialCpaCumulative = 0) => {
  if (row.lockedCpaThisCertificate !== undefined && row.lockedCpaThisCertificate !== null && String(row.lockedCpaThisCertificate) !== "") return numberOrZero(row.lockedCpaThisCertificate);
  if (row.cpaThisCertificate !== undefined && row.cpaThisCertificate !== null && String(row.cpaThisCertificate) !== "") return numberOrZero(row.cpaThisCertificate);
  return getCertificateInitialCpaCumulative(row) - numberOrZero(previousInitialCpaCumulative);
};

const getCertificateCpaThisCertificate = (row = {}, previousCpaCumulative = 0) =>
  getCertificateCurrentCpaCumulative(row) - numberOrZero(previousCpaCumulative);

// Retention base. includeCpa=true → retention applies to the price adjustment
// (CPA): base = Subtotal B + ROE. This is the FIDIC 14.3 position (retention is
// applied to the total of sub-paragraphs (a) work + (b) the 13.7/13.8 price
// adjustment). includeCpa=false → base = Subtotal A + ROE, i.e. CPA excluded
// (the common GCC convention). ROE is always included (it is part of the
// 13.8.3 imported-materials adjustment and is retained either way here).
const getRetentionBase = (subtotalB, subtotalA, roeThisCertificate, includeCpa = false) => {
  const base = includeCpa ? numberOrZero(subtotalB) : numberOrZero(subtotalA);
  return base + numberOrZero(roeThisCertificate);
};

// A blank override keeps retention linked to the contract percentage. Any
// entered amount, including zero, becomes the certificate's retained figure.
const hasManualRetentionOverride = (row = {}) =>
  row.retentionOverride !== undefined &&
  row.retentionOverride !== null &&
  String(row.retentionOverride).trim() !== "";

const getEffectiveRetention = (row = {}, calculatedRetention = 0) =>
  hasManualRetentionOverride(row)
    ? numberOrZero(row.retentionOverride)
    : numberOrZero(calculatedRetention);

const getPenaltyAmount = (row = {}) => {
  const stored = getFirstPositiveNumber(row.penalties, row.penaltyAmount, row.penaltiesAmount);
  if (stored > 0) return stored;
  return numberOrZero(row.penaltiesRatePerDay) * numberOrZero(row.penaltiesDays);
};

const calculateGrossAmountDue = (row = {}, subtotalB = 0, subtotalA = 0, roeThisCertificate = 0) => {
  const retention = numberOrZero(row.retention);
  const penalties = getPenaltyAmount(row);
  return numberOrZero(subtotalB) + numberOrZero(roeThisCertificate) - retention - penalties;
};

const getPaidAmount = (row = {}, fallback = 0) => {
  if (row.isPaid === false) return 0;
  return getFirstPositiveNumber(row.paidAmount, row.amountPaid, row.paymentAmount) || (row.isPaid ? numberOrZero(fallback) : 0);
};

const isCertificatePaid = (row = {}) => {
  if (row.isPaid === true) return true;
  return getFirstPositiveNumber(row.paidAmount, row.amountPaid, row.paymentAmount) > 0;
};

const formatPdfDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(`${dateString}T00:00:00`);
  if (Number.isNaN(date.getTime())) return dateString;
  return new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" }).format(date).replace(/\//g, " ");
};

const addOneDay = (dateString) => {
  if (!dateString) return "";
  const date = new Date(`${dateString}T00:00:00`);
  if (Number.isNaN(date.getTime())) return "";
  date.setDate(date.getDate() + 1);
  return date.toISOString().slice(0, 10);
};

const escapeHtml = (value) =>
  String(value || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#039;");

// ─── 2dp quantity formatter (no floating point garbage) ───────────────────────
const fmt2 = (value) => numberOrZero(value).toFixed(2).replace(/\.00$/, "").replace(/(\.\d)0$/, "$1");

// ─── Shared professional table style ──────────────────────────────────────────
const proTableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 13,
  tableLayout: "auto",
};
const proTh = (extra = {}) => ({
  padding: "9px 12px",
  fontWeight: 700,
  fontSize: 11,
  color: "#374151",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  background: "#f1f5f9",
  borderBottom: "2px solid #cbd5e1",
  borderRight: "1px solid #e2e8f0",
  whiteSpace: "nowrap",
  ...extra,
});
const proTd = (extra = {}) => ({
  padding: "9px 12px",
  borderBottom: "1px solid #e2e8f0",
  borderRight: "1px solid #e2e8f0",
  whiteSpace: "nowrap",
  verticalAlign: "middle",
  ...extra,
});
const proTfoot = (extra = {}) => ({
  padding: "9px 12px",
  fontWeight: 700,
  background: "#f8fafc",
  borderTop: "2px solid #cbd5e1",
  borderRight: "1px solid #e2e8f0",
  whiteSpace: "nowrap",
  ...extra,
});

// ─── Column definitions ────────────────────────────────────────────────────────

const CERTIFICATE_TABLE_COLUMNS = [
  { key: "certificateNo", label: "Certificate No", defaultVisible: true },
  { key: "certificateDate", label: "Date", defaultVisible: true },
  { key: "certificateMonth", label: "Month", defaultVisible: true },
  { key: "subtotalA", label: "Subtotal A", defaultVisible: true },
  { key: "subtotalB", label: "Subtotal B", defaultVisible: true },
  { key: "scheduledWorks", label: "Scheduled Works", defaultVisible: false },
  { key: "extraWork", label: "Extra Work", defaultVisible: false },
  { key: "cpaInitial", label: "CPA Initial", defaultVisible: false },
  { key: "cpaCurrent", label: "CPA Current", defaultVisible: false },
  { key: "cpaLock", label: "CPA Lock", defaultVisible: false },
  { key: "roe", label: "ROE", defaultVisible: true },
  { key: "retention", label: "Retention", defaultVisible: true },
  { key: "penalties", label: "Penalties", defaultVisible: false },
  { key: "grossAmountDue", label: "Gross Amount Due", defaultVisible: true },
  { key: "previousPayments", label: "Previous Payments", defaultVisible: true },
  { key: "nettAmount", label: "Nett Amount", defaultVisible: true },
  { key: "vat", label: "VAT", defaultVisible: true },
  { key: "amountNowDue", label: "Amount Now Due", defaultVisible: true },
  { key: "paidAmount", label: "Paid Amount", defaultVisible: true },
  { key: "notes", label: "Notes", defaultVisible: false },
  { key: "actions", label: "Actions", defaultVisible: true },
];

const DEFAULT_CERTIFICATE_VISIBLE_COLUMNS = CERTIFICATE_TABLE_COLUMNS.reduce((acc, col) => {
  acc[col.key] = col.defaultVisible;
  return acc;
}, {});

// ─── Shared button style (pill, consistent with rounded app nav) ───────────────

const btn = (bg, extra = {}) => ({
  background: bg,
  color: "#fff",
  border: "none",
  borderRadius: 9999,
  padding: "6px 16px",
  fontWeight: 600,
  fontSize: 13,
  cursor: "pointer",
  whiteSpace: "nowrap",
  lineHeight: 1.4,
  ...extra,
});

const btnOutline = (extra = {}) => ({
  background: "transparent",
  color: "#374151",
  border: "1.5px solid #d1d5db",
  borderRadius: 9999,
  padding: "6px 16px",
  fontWeight: 600,
  fontSize: 13,
  cursor: "pointer",
  whiteSpace: "nowrap",
  lineHeight: 1.4,
  ...extra,
});

// ─── Shared modal overlay ──────────────────────────────────────────────────────

function CertificateModal({ title, subtitle, onClose, headerActions, children }) {
  const handleBackdrop = (e) => { if (e.target === e.currentTarget) onClose(); };
  return (
    <div onClick={handleBackdrop} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", backdropFilter: "blur(2px)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "24px 16px 40px", overflowY: "auto" }}>
      <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 880, boxShadow: "0 24px 64px rgba(0,0,0,0.22)", display: "flex", flexDirection: "column", maxHeight: "calc(100vh - 64px)", overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 24px 16px", borderBottom: "1px solid #e5e7eb", flexShrink: 0, gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 18, color: "#0f172a" }}>{title}</div>
            {subtitle && <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>{subtitle}</div>}
          </div>
          <div style={{ display: "flex", gap: 8, flexShrink: 0, flexWrap: "wrap" }}>
            {headerActions}
            <button type="button" onClick={onClose} style={btnOutline()}>✕ Close</button>
          </div>
        </div>
        <div style={{ overflowY: "auto", padding: "20px 24px 28px", flex: 1 }}>{children}</div>
      </div>
    </div>
  );
}

// ─── Form row helper ───────────────────────────────────────────────────────────

function CertificateFormRow({ label, helper, children }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "220px 1fr", gap: 14, alignItems: "start", marginBottom: 10 }}>
      <div>
        <label style={{ fontWeight: 700, fontSize: 13, color: "#0f172a" }}>{label}</label>
        {helper && <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2, lineHeight: 1.4 }}>{helper}</div>}
      </div>
      <div style={{ minWidth: 0 }}>{children}</div>
    </div>
  );
}

// ─── Summary pills ─────────────────────────────────────────────────────────────

function SummaryPills({ items }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, margin: "14px 0", padding: "12px 14px", background: "#f8fafc", borderRadius: 12, border: "1px solid #e5e7eb" }}>
      {items.map(({ label, value, accent }) => (
        <div key={label} style={{ flex: "1 1 120px", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: "10px 12px" }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>{label}</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: accent || "#0f172a" }}>{value}</div>
        </div>
      ))}
    </div>
  );
}

// ─── Section toggle button (for Monthly Valuation / Approved VOs in edit modal) ─

function SectionToggleBtn({ label, count, amount, active, color, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        flex: "1 1 0",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        padding: "12px 16px",
        borderRadius: 12,
        border: active ? `2px solid ${color}` : "2px solid #e5e7eb",
        background: active ? `${color}12` : "#fff",
        cursor: "pointer",
        transition: "all 0.15s",
      }}
    >
      <div style={{ fontWeight: 700, fontSize: 14, color: active ? color : "#374151" }}>{label}</div>
      <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>{count} selected · {amount}</div>
      <div style={{ marginTop: 6, fontSize: 11, fontWeight: 600, color: active ? color : "#9ca3af" }}>
        {active ? "▲ Hide table" : "▼ Open table"}
      </div>
    </button>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────

function CertificatesSection({
  certificateForm,
  setCertificateForm,
  handleAddOrUpdateCertificate,
  certificates = [],
  currentProjectId = "",
  editingCertificateId,
  handleEditCertificate,
  handleUpdateCertificatePayment,
  handleDeleteCertificate,
  clearCertificateForm,
  handleViewItem,
  projectDetails = {},
  availableMonths = [],
  latestCpaCumulative = 0,
  currentContractType = "GCC",
  latestCpaIndexStatus = "",
  soqItems = [],
  variations = [],
  roeRows = [],
  getRoeThisClaimForCertificate,
  getRoeThisClaimForMonth,
  cpaByCertificateNo = {},
  claims = [],
  handleSetCertificateApproval,
}) {
  // Does retention apply to escalation (CPA)? FIDIC 14.3 retains on the price
  // adjustment; GCC convention usually excludes it. An explicit per-project
  // setting wins; otherwise derive from the contract type.
  const retentionOnCpa = projectDetails?.retentionOnCpa !== undefined
    ? !!projectDetails.retentionOnCpa
    : /fidic/i.test(String(currentContractType || ""));
  const persistRetentionOnCpa = async (value) => {
    if (!currentProjectId) { alert("Select a project first."); return; }
    try {
      await updateDoc(doc(db, "projects", currentProjectId), { retentionOnCpa: value });
    } catch (e) { alert(`Could not save the retention setting: ${e.message || e}`); }
  };

  const fileInputRef = useRef(null);
  const [valuationSearchText, setValuationSearchText] = useState("");
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  const [valuationSectionFilter, setValuationSectionFilter] = useState("All");
  const [excelImportMessage, setExcelImportMessage] = useState("");
  const [historyRow, setHistoryRow] = useState(null);
  const [excelImportError, setExcelImportError] = useState("");
  // ── Excel import column-mapping workflow ──
  const [showImportHelper, setShowImportHelper] = useState(false);   // pre-upload helper popup
  const [importSheetRows, setImportSheetRows] = useState(null);      // raw array-of-arrays from the sheet (null = no file loaded)
  const [importFileName, setImportFileName] = useState("");
  const [importItemCol, setImportItemCol] = useState(0);             // chosen column index for item numbers
  const [importQtyCol, setImportQtyCol] = useState(1);               // chosen column index for quantities
  const [importSkipFirstRow, setImportSkipFirstRow] = useState(true);// treat first row as a header to skip
  const [visibleCertificateColumns, setVisibleCertificateColumns] = useState(DEFAULT_CERTIFICATE_VISIBLE_COLUMNS);
  const [showColumnPicker, setShowColumnPicker] = useState(false);

  // Robust cert-no lookup into cpaByCertificateNo — handles leading zeros, case, numeric match
  const lookupCpaData = (certNo) => {
    if (!cpaByCertificateNo || !certNo) return null;
    const s = String(certNo).trim();
    const tries = [s, s.toLowerCase(), s.toUpperCase(), String(parseInt(s, 10)), s.padStart(3, "0")];
    for (const key of tries) { if (cpaByCertificateNo[key]) return cpaByCertificateNo[key]; }
    const n = parseFloat(s.replace(/[^0-9.]/g, ""));
    if (!isNaN(n)) {
      return Object.values(cpaByCertificateNo).find((v) =>
        parseFloat(String(v.certificateNo || "").replace(/[^0-9.]/g, "")) === n
      ) || null;
    }
    return null;
  };

  const getCpaThisCertificateFromRegister = (certNo, useCurrentCertificateProvisional = true) => {
    const cpaData = lookupCpaData(certNo);
    if (!cpaData) return 0;

    if (Array.isArray(cpaData.perTable) && cpaData.perTable.length) {
      return cpaData.perTable.reduce((sum, table) => {
        const provisional = numberOrZero(
          table.provisionalCpaThisCertificate ??
          table.provisionalAmount ??
          table.provisionalCpaAmount ??
          table.cpaThisCertificateProvisional
        );

        const ruling = numberOrZero(
          table.rulingCpaThisCertificate ??
          table.finalCpaThisCertificate ??
          table.finalAmount ??
          table.rulingAmount ??
          table.cpaThisCertificate
        );

        return sum + (useCurrentCertificateProvisional ? provisional : ruling);
      }, 0);
    }

    if (cpaData.certHistory && Array.isArray(cpaData.certHistory) && cpaData.certHistory.length) {
      const targetNo = String(cpaData.certificateNo || certNo || "").replace(/^0+/, "");
      const historyRow = cpaData.certHistory.find((h) => String(h.certNo || "").replace(/^0+/, "") === targetNo);
      if (historyRow?.perTable) {
        return Object.values(historyRow.perTable).reduce((sum, table) => {
          const provisional = numberOrZero(table.provisionalAmount ?? table.provisionalCpaThisCertificate ?? 0);
          const ruling = numberOrZero(table.rulingAmount ?? table.finalAmount ?? table.rulingCpaThisCertificate ?? 0);
          return sum + (useCurrentCertificateProvisional ? provisional : ruling);
        }, 0);
      }
    }

    return numberOrZero(
      useCurrentCertificateProvisional
        ? cpaData.provisionalCpaThisCertificate
        : cpaData.rulingCpaThisCertificate
    );
  };

  // Modal state: null | "view" | "edit" | "add"
  const [modalMode, setModalMode] = useState(null);
  // The row being viewed/edited
  const [viewingRow, setViewingRow] = useState(null);

  // Change 3: selected certificate filter — null = "All", otherwise row id
  const [selectedCertId, setSelectedCertId] = useState(null); // null = all; "LAST" = most recent

  // Change 2: which section is open in edit modal
  const [editSectionOpen, setEditSectionOpen] = useState(null); // null | "valuation" | "vos"

  const visibleCertificateColumnCount = CERTIFICATE_TABLE_COLUMNS.filter((c) => visibleCertificateColumns[c.key]).length;
  const toggleCertificateColumn = (key) => setVisibleCertificateColumns((prev) => ({ ...prev, [key]: !prev[key] }));
  const resetCertificateColumns = () => setVisibleCertificateColumns(DEFAULT_CERTIFICATE_VISIBLE_COLUMNS);

  const safeCertificateForm = certificateForm || {};
  const updateCertificateForm = typeof setCertificateForm === "function" ? setCertificateForm : () => {};
  const currentCpaIndexStatus = String(safeCertificateForm.lockedCpaIndexStatus || latestCpaIndexStatus || "").toUpperCase();
  const cpaIndexStatusLabel = currentCpaIndexStatus === "F" ? "Final indices" : currentCpaIndexStatus === "P" ? "Provisional indices" : "Index status not set";

  // ── Sorted / effective rows ──

  const rows = useMemo(() => (
    [...(certificates || [])].filter((r) => !currentProjectId || r.projectId === currentProjectId).sort(compareCertificateRows)
  ), [certificates, currentProjectId]);

  const effectiveRows = useMemo(() => {
    let runningSubtotalA = 0;
    let runningRulingCpaCumulative = 0;
    let runningRoeCumulative = 0;
    let runningCpaScheduled = 0;

    return rows.map((row) => {
      const scheduledWorks = buildClaimedItemsSubtotal(row.claimedItems || []);
      const cpaScheduledThisCert = buildCpaSubjectSubtotal(row.claimedItems || []);
      runningCpaScheduled = runningCpaScheduled + cpaScheduledThisCert;
      const effectiveCpaScheduledCumulative = runningCpaScheduled;
      const extraWork = buildExtraWorkSubtotal(row.extraWorkItems || []);
      const claimsWork = buildClaimsSubtotal(row.claimsItems || []);
      const storedSubtotalA = numberOrZero(row.subtotalA);
      const calculatedSubtotalA = runningSubtotalA + scheduledWorks + extraWork + claimsWork;

      const hasBreakdownItems =
        (row.claimedItems || []).length > 0 ||
        (row.extraWorkItems || []).length > 0 ||
        (row.claimsItems || []).length > 0;

      // If the certificate has detail rows, rebuild Subtotal A from the detail rows.
      // Only fall back to stored subtotalA where no detail rows exist.
      const effectiveSubtotalA = hasBreakdownItems
        ? calculatedSubtotalA
        : storedSubtotalA > 0
          ? storedSubtotalA
          : calculatedSubtotalA;

      runningSubtotalA = effectiveSubtotalA;

      const certNo = String(row.certificateNo || row.id || "").trim();
      const certMonth = String(row.certificateMonth || "").trim();
      const cpaData = lookupCpaData(certNo);

      let currentCpaCumulative;
      if (hasExplicitCertificateCpaLock(row)) {
        currentCpaCumulative = numberOrZero(row.lockedCpaCumulative ?? row.cpaCumulative);
      } else if (cpaData) {
        currentCpaCumulative = roundToCertificate(
          runningRulingCpaCumulative + getCpaThisCertificateFromRegister(certNo, true)
        );
      } else {
        currentCpaCumulative = numberOrZero(row.currentCpaCumulative ?? row.liveCpaCumulative ?? row.cpaCumulative ?? 0);
      }

      const rulingThisCert = cpaData
        ? getCpaThisCertificateFromRegister(certNo, false)
        : (hasExplicitCertificateCpaLock(row)
            ? numberOrZero(row.lockedCpaThisCertificate ?? row.cpaThisCertificate)
            : 0);
      runningRulingCpaCumulative = roundToCertificate(runningRulingCpaCumulative + rulingThisCert);

      // ROE cumulative — add this cert's ROE to the running total
      const roeThisCert = numberOrZero(row.roeThisCertificate)
        || (typeof getRoeThisClaimForCertificate === "function" ? getRoeThisClaimForCertificate(roeRows, certNo) : 0)
        || (typeof getRoeThisClaimForMonth === "function" ? getRoeThisClaimForMonth(roeRows, certMonth) : 0);
      runningRoeCumulative = roundToCertificate(runningRoeCumulative + roeThisCert);

      const effectiveSubtotalB = effectiveSubtotalA + currentCpaCumulative;

      return {
        ...row,
        effectiveScheduledWorks: scheduledWorks,
        effectiveExtraWork: extraWork,
        effectiveSubtotalA,
        effectiveCpaScheduledCumulative,
        currentCpaCumulative,
        effectiveSubtotalB,
        effectiveRoeCumulative: runningRoeCumulative,
        effectiveRoeThisCert: roeThisCert,
      };
    });
  }, [rows, cpaByCertificateNo, latestCpaCumulative, roeRows, getRoeThisClaimForCertificate, getRoeThisClaimForMonth]);

  // Change 3: filtered rows shown in the table
  const displayedRows = useMemo(() => {
    if (!selectedCertId || selectedCertId === "ALL") return effectiveRows;
    return effectiveRows.filter((r) => r.id === selectedCertId);
  }, [effectiveRows, selectedCertId]);

  const previousRows = useMemo(() => {
    const currentDate = safeCertificateForm.certificateDate || "9999-12-31";
    return effectiveRows.filter((r) => {
      if (editingCertificateId && r.id === editingCertificateId) return false;
      return (r.certificateDate || "") < currentDate;
    });
  }, [effectiveRows, safeCertificateForm.certificateDate, editingCertificateId]);

  const previousRow = previousRows.length ? previousRows[previousRows.length - 1] : null;
  const previousSubtotalA = previousRow ? numberOrZero(previousRow.effectiveSubtotalA) : 0;
  const previousCpaScheduledCumulative = previousRow ? numberOrZero(previousRow.effectiveCpaScheduledCumulative) : 0;
  const previousSubtotalB = previousRow ? getCertificateSubtotalB(previousRow) : 0;
  const previousCpaCumulative = previousRow ? getCertificateCpaCumulative(previousRow) : 0;

  // ── SOQ / valuation ──

  const projectSoqItems = useMemo(() => (
    (soqItems || []).filter((i) => !currentProjectId || i.projectId === currentProjectId)
      .sort((a, b) => String(a.itemNo || "").localeCompare(String(b.itemNo || ""), undefined, { numeric: true, sensitivity: "base" }))
  ), [soqItems, currentProjectId]);

  const valuationRows = useMemo(() => {
    return projectSoqItems.map((item) => {
      const previousQty = previousRows.reduce((sum, cert) => {
        const matched = (cert.claimedItems || []).find((e) => normalizeItemNo(e.itemNo) === normalizeItemNo(item.itemNo));
        return sum + numberOrZero(matched?.thisMonthQty);
      }, 0);
      const existing = (safeCertificateForm.claimedItems || []).find((e) => normalizeItemNo(e.itemNo) === normalizeItemNo(item.itemNo));
      const rate = numberOrZero(item.tenderRate ?? item.rate);
      const thisMonthQty = existing?.thisMonthQty ?? "";
      const cumulativeQty = previousQty + numberOrZero(thisMonthQty);
      return { itemId: item.id || item.itemNo, itemNo: item.itemNo, description: item.description, unit: item.unit, rate, previousQty, thisMonthQty, cumulativeQty, amountThisMonth: (numberOrZero(thisMonthQty) * rate), amountToDate: (cumulativeQty * rate), finalEstimatedQty: numberOrZero(item.finalEstimatedQty), tenderQty: numberOrZero(item.tenderQty), sectionNo: item.sectionNo || "", sectionName: item.sectionName || "" };
    });
  }, [projectSoqItems, previousRows, safeCertificateForm.claimedItems]);

  const valuationToDateTotal = useMemo(() => valuationRows.reduce((s, r) => s + numberOrZero(r.amountToDate), 0), [valuationRows]);

  const approvedVariationRows = useMemo(() => {
    return (variations || [])
      .filter((v) => (!currentProjectId || v.projectId === currentProjectId) && String(v.status || "").trim().toLowerCase() === "approved")
      .sort((a, b) => String(a.variationNo || "").localeCompare(String(b.variationNo || ""), undefined, { numeric: true }))
      .map((variation) => {
        const approvedValue = getVariationApprovedValue(variation);
        const approvedQty = getVariationApprovedQty(variation);
        const rate = getVariationRate(variation, approvedValue, approvedQty);
        const previouslyCertifiedQty = previousRows.reduce((sum, cert) => {
          const matched = (cert.extraWorkItems || []).find((e) => e.variationId === variation.id);
          return sum + numberOrZero(matched?.quantityThisCertificate);
        }, 0);
        const existing = (safeCertificateForm.extraWorkItems || []).find((e) => e.variationId === variation.id);
        const quantityThisCertificate = existing?.quantityThisCertificate ?? "";
        const cumulativeQty = previouslyCertifiedQty + numberOrZero(quantityThisCertificate);
        return { variationId: variation.id, variationNo: variation.variationNo, title: variation.title || variation.voDescription, description: variation.description || variation.voDescription, approvedValue, approvedQty, rate, previouslyCertifiedQty, quantityThisCertificate, cumulativeQty, amountThisCertificate: (numberOrZero(quantityThisCertificate) * rate), balanceQty: approvedQty - cumulativeQty, balanceValue: (approvedValue - cumulativeQty * rate) };
      });
 }, [variations, currentProjectId, previousRows, safeCertificateForm.extraWorkItems]);

const cleanExtraWorkItems = (items = []) =>
  syncSelectedVariationItems(items, approvedVariationRows);

const validExtraWorkItems = useMemo(

  () => syncSelectedVariationItems(safeCertificateForm.extraWorkItems || [], approvedVariationRows),

  [safeCertificateForm.extraWorkItems, approvedVariationRows]

);

// ── Computed certificate form values ──

const scheduledWorksThisCertificate = buildClaimedItemsSubtotal(safeCertificateForm.claimedItems || []);

const extraWorkThisCertificate = buildExtraWorkSubtotal(validExtraWorkItems);
  const claimsThisCertificate = buildClaimsSubtotal(safeCertificateForm.claimsItems || []);
  const calculatedSubtotalACumulative =
  previousSubtotalA +
  scheduledWorksThisCertificate +
  extraWorkThisCertificate +
  claimsThisCertificate;

  // Cumulative escalatable base to date (only "subject to CPA" scheduled items).
  // Persisted so the CPA section can auto-fill "Total scheduled items" per month.
  const cpaScheduledItemsCumulative =
    previousCpaScheduledCumulative + buildCpaSubjectSubtotal(safeCertificateForm.claimedItems || []);

const storedFormSubtotalA = numberOrZero(safeCertificateForm.subtotalA);

const formHasBreakdownItems =
  (safeCertificateForm.claimedItems || []).length > 0 ||
  validExtraWorkItems.length > 0 ||
  (safeCertificateForm.claimsItems || []).length > 0;

const subtotalACumulative =
  formHasBreakdownItems
    ? calculatedSubtotalACumulative
    : storedFormSubtotalA > 0 && editingCertificateId
      ? storedFormSubtotalA
      : calculatedSubtotalACumulative;
  const hasLockedCpaCumulative = safeCertificateForm.lockedCpaCumulative !== undefined && safeCertificateForm.lockedCpaCumulative !== null && String(safeCertificateForm.lockedCpaCumulative) !== "";

  // For the form being edited/added: find CPA data for this cert from the FIDIC register
  const formCertNo = String(safeCertificateForm.certificateNo || "").trim();
  const formCpaData = lookupCpaData(formCertNo);

  // previousRulingCpaCumulative: cumulative ruling CPA of all certs before this one
  const previousRulingCpaCumulative = (() => {
    if (!previousRow) return 0;
    const prevIdx = effectiveRows.findIndex((r) => r.id === previousRow.id);
    if (prevIdx < 0) return 0;
    let runningRuling = 0;
    for (let i = 0; i <= prevIdx; i++) {
      const r = effectiveRows[i];
      const rCpaData = lookupCpaData(String(r.certificateNo || r.id || "").trim());
      if (hasExplicitCertificateCpaLock(r)) {
        runningRuling = roundToCertificate(runningRuling + numberOrZero(r.lockedCpaThisCertificate ?? r.cpaThisCertificate));
      } else if (rCpaData) {
        runningRuling = roundToCertificate(runningRuling + getCpaThisCertificateFromRegister(String(r.certificateNo || r.id || "").trim(), false));
      }
    }
    return runningRuling;
  })();

  const effectiveCpaCumulative = hasLockedCpaCumulative
    ? numberOrZero(safeCertificateForm.lockedCpaCumulative)
    : formCpaData
      ? roundToCertificate(previousRulingCpaCumulative + getCpaThisCertificateFromRegister(formCertNo, true))
      : numberOrZero(latestCpaCumulative);
  const cpaThisCertificate = (effectiveCpaCumulative - previousCpaCumulative);
  const subtotalBCumulative = (subtotalACumulative + effectiveCpaCumulative);
  const retentionPercentage = getRetentionPercentage(safeCertificateForm);

  const roeThisCertificate = useMemo(() => {
    const certNo = String(safeCertificateForm.certificateNo || "").trim();
    const certMonth = String(safeCertificateForm.certificateMonth || "").trim();
    const byCertificate = typeof getRoeThisClaimForCertificate === "function" ? getRoeThisClaimForCertificate(roeRows, certNo) : 0;
    const byMonth = typeof getRoeThisClaimForMonth === "function" ? getRoeThisClaimForMonth(roeRows, certMonth) : 0;
    return (byCertificate || byMonth || safeCertificateForm.roeThisCertificate);
  }, [safeCertificateForm.certificateNo, safeCertificateForm.certificateMonth, safeCertificateForm.roeThisCertificate, getRoeThisClaimForCertificate, getRoeThisClaimForMonth, roeRows]);

  const penalties = (numberOrZero(safeCertificateForm.penaltiesRatePerDay) * numberOrZero(safeCertificateForm.penaltiesDays));
  const calculatedRetention = (getRetentionBase(subtotalBCumulative, subtotalACumulative, roeThisCertificate, retentionOnCpa) * retentionPercentage);
  const retentionIsManuallyOverridden = hasManualRetentionOverride(safeCertificateForm);
  const retention = getEffectiveRetention(safeCertificateForm, calculatedRetention);
  const grossAmountDue = calculateGrossAmountDue({ ...safeCertificateForm, retention, penalties }, subtotalBCumulative, subtotalACumulative, roeThisCertificate);

  const previousPayments = useMemo(() => {
    if (!previousRow) return 0;
    const prevA = numberOrZero(previousRow.effectiveSubtotalA || previousRow.subtotalA);
    const prevB = getCertificateSubtotalB({ ...previousRow, effectiveSubtotalA: prevA });
    const prevRoe = numberOrZero(previousRow.roeThisCertificate);
    const prevCalculatedRetention = getRetentionBase(prevB, prevA, prevRoe, retentionOnCpa) * getRetentionPercentage(previousRow);
    const prevRet = getEffectiveRetention(previousRow, prevCalculatedRetention);
    const prevPen = getPenaltyAmount(previousRow);
    const prevGross = calculateGrossAmountDue({ ...previousRow, retention: prevRet, penalties: prevPen }, prevB, prevA, prevRoe);
    return getPaidAmount(previousRow, prevGross);
  }, [previousRow]);

  const nettAmount = (grossAmountDue - previousPayments);
  const vat = (nettAmount * 0.15);
  const amountNowDue = (nettAmount + vat);

  useEffect(() => {
    if (typeof updateCertificateForm !== "function") return;
    const nextValues = {
      subtotalA: String(subtotalACumulative), subtotalB: String(subtotalBCumulative),
      cpaScheduledItemsCumulative: String(cpaScheduledItemsCumulative),
      cpaCumulative: String(effectiveCpaCumulative), cpaThisCertificate: String(cpaThisCertificate),
      lockedCpaCumulative: hasLockedCpaCumulative ? String(effectiveCpaCumulative) : safeCertificateForm.lockedCpaCumulative || "",
      lockedCpaThisCertificate: hasLockedCpaCumulative ? String(cpaThisCertificate) : safeCertificateForm.lockedCpaThisCertificate || "",
      lockedCpaCertifiedValue: hasLockedCpaCumulative ? String(cpaThisCertificate) : safeCertificateForm.lockedCpaCertifiedValue || "",
      lockedCpaSource: hasLockedCpaCumulative ? safeCertificateForm.lockedCpaSource || "Snapshot taken from CPA values available when certificate was saved" : safeCertificateForm.lockedCpaSource || "",
      lockedCpaLockedAt: hasLockedCpaCumulative ? safeCertificateForm.lockedCpaLockedAt || new Date().toISOString() : safeCertificateForm.lockedCpaLockedAt || "",
      roeThisCertificate: String(roeThisCertificate), retentionPercentage: String(retentionPercentage),
      penalties: String(penalties), calculatedRetention: String(calculatedRetention), retention: String(retention), grossAmountDue: String(grossAmountDue),
      previousPayments: String(previousPayments), nettAmount: String(nettAmount), vat: String(vat),
      amountNowDue: String(amountNowDue), projectId: currentProjectId,
extraWorkItems: validExtraWorkItems,
    };
    const isSame = Object.entries(nextValues).every(([k, v]) => String(safeCertificateForm[k] ?? "") === String(v ?? ""));
    if (isSame) return;
    updateCertificateForm((prev) => ({ ...prev, ...nextValues }));
  }, [updateCertificateForm, safeCertificateForm, subtotalACumulative, subtotalBCumulative, effectiveCpaCumulative, cpaThisCertificate, roeThisCertificate, retentionPercentage, penalties, calculatedRetention, retention, grossAmountDue, previousPayments, nettAmount, vat, amountNowDue, currentProjectId, validExtraWorkItems]);

  // ── Approved P&G from Claims (for edit modal tile) ──
  const approvedPngItems = useMemo(() => {
    const projectClaims = (claims || []).filter((c) => c.projectId === currentProjectId && (c.pgRatePerDay || c.pgRateDay));
    return projectClaims.map((claim) => {
      const rate = numberOrZero(claim.pgRatePerDay || claim.pgRateDay);
      const daysAwarded = numberOrZero(claim.daysAwarded || claim.daysClaimed);
      const totalAuthorised = rate * daysAwarded;
      const certifiedDays = numberOrZero(
        ((safeCertificateForm.claimsItems || []).find((ci) => ci.claimId === claim.id))?.daysThisCertificate
      );
      const certifiedAmt = numberOrZero(
        ((safeCertificateForm.claimsItems || []).find((ci) => ci.claimId === claim.id))?.amountThisCertificate
      );
      return { claimId: claim.id, claimNo: claim.claimNo, title: claim.title || claim.detail, rate, daysAwarded, totalAuthorised, certifiedDays, certifiedAmt };
    });
  }, [claims, currentProjectId, safeCertificateForm.claimsItems]);

  const approvedPngTotal = useMemo(() =>
    approvedPngItems.reduce((sum, item) => sum + item.totalAuthorised, 0),
  [approvedPngItems]);

  const approvedPngCertifiedAmt = useMemo(() =>
    approvedPngItems.reduce((sum, item) => sum + item.certifiedAmt, 0),
  [approvedPngItems]);

  const filteredValuationRows = useMemo(() => {
    const selectedNos = new Set((safeCertificateForm.claimedItems || []).map((i) => normalizeItemNo(i.itemNo)));
    return valuationRows.filter((row) => {
      if (!matchesSection(row, valuationSectionFilter)) return false;
      const hay = [row.itemNo, row.description, row.unit].join(" ").toLowerCase();
      return (!valuationSearchText.trim() || hay.includes(valuationSearchText.toLowerCase())) && (!showSelectedOnly || selectedNos.has(normalizeItemNo(row.itemNo)));
    });
  }, [valuationRows, valuationSearchText, valuationSectionFilter, showSelectedOnly, safeCertificateForm.claimedItems]);

  const patchCertificateForm = (patch) => updateCertificateForm({ ...safeCertificateForm, ...patch, projectId: currentProjectId });

  // ── CPA lock ──

  const handleToggleCpaLock = (row, isLocked, currentCpaCumulative, currentCpaThisCertificate) => {
    if (typeof handleUpdateCertificatePayment !== "function") return;
    if (isLocked) {
      handleUpdateCertificatePayment(row, { isCpaLocked: true, lockedCpaCumulative: currentCpaCumulative, lockedCpaThisCertificate: currentCpaThisCertificate, lockedCpaCertifiedValue: currentCpaThisCertificate, lockedCpaIndexStatus: latestCpaIndexStatus || row.lockedCpaIndexStatus || "P", lockedCpaLockedAt: new Date().toISOString(), lockedCpaSource: "Manual CPA snapshot from certificate table" });
    } else {
      handleUpdateCertificatePayment(row, { isCpaLocked: false, lockedCpaCumulative: "", lockedCpaThisCertificate: "", lockedCpaCertifiedValue: "", lockedCpaIndexStatus: "", lockedCpaLockedAt: "", lockedCpaSource: "" });
    }
  };

  // ── Claim item handlers ──

  const handleToggleClaimItem = (row, isChecked) => {
    const existing = Array.isArray(safeCertificateForm.claimedItems) ? safeCertificateForm.claimedItems : [];
    if (!isChecked) { patchCertificateForm({ claimedItems: existing.filter((i) => normalizeItemNo(i.itemNo) !== normalizeItemNo(row.itemNo)) }); return; }
    if (existing.some((i) => normalizeItemNo(i.itemNo) === normalizeItemNo(row.itemNo))) return;
    // subjectToCpa defaults ON — a captured figure is escalatable unless the engineer opts it out.
    patchCertificateForm({ claimedItems: [...existing, { itemNo: row.itemNo, description: row.description, unit: row.unit, previousQty: numberOrZero(row.previousQty), thisMonthQty: "", cumulativeQty: numberOrZero(row.previousQty), rate: numberOrZero(row.rate), amountThisMonth: 0, amountToDate: (numberOrZero(row.previousQty) * numberOrZero(row.rate)), subjectToCpa: true }] });
  };

  const handleClaimQtyChange = (row, value) => {
    const rate = numberOrZero(row.rate), previousQty = numberOrZero(row.previousQty), thisMonthQty = numberOrZero(value);
    const prior = (safeCertificateForm.claimedItems || []).find((ci) => normalizeItemNo(ci.itemNo) === normalizeItemNo(row.itemNo));
    const updatedItem = { itemNo: row.itemNo, description: row.description, unit: row.unit, previousQty, thisMonthQty: value, cumulativeQty: previousQty + thisMonthQty, rate, amountThisMonth: (thisMonthQty * rate), amountToDate: ((previousQty + thisMonthQty) * rate), subjectToCpa: prior?.subjectToCpa !== false };
    patchCertificateForm({ claimedItems: [...((safeCertificateForm.claimedItems || []).filter((ci) => normalizeItemNo(ci.itemNo) !== normalizeItemNo(row.itemNo))), updatedItem].sort((a, b) => String(a.itemNo || "").localeCompare(String(b.itemNo || ""), undefined, { numeric: true })) });
  };

  // Toggle whether a claimed item is escalatable (feeds the cumulative CPA base).
  const handleToggleClaimCpa = (row, isChecked) => {
    patchCertificateForm({ claimedItems: (safeCertificateForm.claimedItems || []).map((ci) =>
      normalizeItemNo(ci.itemNo) === normalizeItemNo(row.itemNo) ? { ...ci, subjectToCpa: isChecked } : ci) });
  };

  // ── Phase 4: capture a contractor's monthly submission ──────────────────────
  // Reads an uploaded submission (Excel/PDF/scan) with AI, matches each pay item to
  // the project SOQ, and pre-fills the monthly valuation. Marks the certificate
  // "awaiting approval" so the engineer reviews before it counts. Extract-only —
  // nothing is saved until the engineer clicks Save.
  const contractorFileRef = useRef(null);
  const [scanBusy, setScanBusy] = useState(false);
  const [scanMsg, setScanMsg] = useState(null); // { type, text }

  const handleContractorSubmission = async (file) => {
    if (!file) return;
    setScanBusy(true);
    setScanMsg({ type: "info", text: `Reading "${file.name}"… this can take a moment for a large sheet.` });
    try {
      const { items, periodMonth } = await scanContractorSubmission(file);
      if (!items.length) {
        setScanMsg({ type: "warn", text: "No pay-item quantities could be read from that file. Try a clearer copy, or capture manually." });
        return;
      }
      const nextClaimed = [...(safeCertificateForm.claimedItems || [])];
      let matched = 0;
      const unmatched = [];
      for (const it of items) {
        const vRow = valuationRows.find((r) => normalizeItemNo(r.itemNo) === normalizeItemNo(it.itemNo));
        if (!vRow) { unmatched.push(it.itemNo); continue; }
        const qty = numberOrZero(it.quantityThisMonth);
        const rate = numberOrZero(vRow.rate), previousQty = numberOrZero(vRow.previousQty);
        const built = { itemNo: vRow.itemNo, description: vRow.description, unit: vRow.unit, previousQty, thisMonthQty: String(qty), cumulativeQty: previousQty + qty, rate, amountThisMonth: (qty * rate), amountToDate: ((previousQty + qty) * rate), subjectToCpa: true };
        const idx = nextClaimed.findIndex((ci) => normalizeItemNo(ci.itemNo) === normalizeItemNo(vRow.itemNo));
        if (idx >= 0) nextClaimed[idx] = built; else nextClaimed.push(built);
        matched++;
      }
      const patch = {
        claimedItems: nextClaimed.sort((a, b) => String(a.itemNo || "").localeCompare(String(b.itemNo || ""), undefined, { numeric: true })),
        status: "awaiting_approval",
      };
      if (periodMonth && !safeCertificateForm.certificateMonth) patch.certificateMonth = periodMonth;
      patchCertificateForm(patch);
      const uTxt = unmatched.length ? ` ${unmatched.length} item${unmatched.length > 1 ? "s" : ""} didn't match the SOQ (${unmatched.slice(0, 8).join(", ")}${unmatched.length > 8 ? "…" : ""}) — add those manually.` : "";
      setScanMsg({ type: matched ? "ok" : "warn", text: `Matched ${matched} item${matched === 1 ? "" : "s"} from the submission and pre-filled the valuation (Subject to CPA on).${uTxt} Review, set the certificate number, then Save — it will be flagged "awaiting approval" for you to approve.` });
    } catch (err) {
      setScanMsg({ type: "error", text: err?.message || "Could not read the submission." });
    } finally {
      setScanBusy(false);
      if (contractorFileRef.current) contractorFileRef.current.value = "";
    }
  };

  const handleToggleExtraWorkItem = (row, isChecked) => {
    const existing = cleanExtraWorkItems(
  Array.isArray(safeCertificateForm.extraWorkItems) ? safeCertificateForm.extraWorkItems : []
);
    if (!isChecked) { patchCertificateForm({ extraWorkItems: existing.filter((i) => i.variationId !== row.variationId) }); return; }
    if (existing.some((i) => i.variationId === row.variationId)) return;
    patchCertificateForm({
      extraWorkItems: [
        ...existing,
        {
          variationId: row.variationId,
          variationNo: row.variationNo,
          title: row.title,
          description: row.description,
          approvedQty: numberOrZero(row.approvedQty),
          previouslyCertifiedQty: numberOrZero(row.previouslyCertifiedQty),
          quantityThisCertificate: "",
          cumulativeQty: numberOrZero(row.previouslyCertifiedQty),
          rate: numberOrZero(row.rate),
          approvedValue: numberOrZero(row.approvedValue),
          amountThisCertificate: 0,
          paymentCertificateVoAmount: 0,
          balanceValue: numberOrZero(row.approvedValue) - numberOrZero(row.previouslyCertifiedQty) * numberOrZero(row.rate),
        },
      ],
    });
  };

  const handleExtraWorkQtyChange = (row, value) => {
    const qty = numberOrZero(value), rate = numberOrZero(row.rate), prevQty = numberOrZero(row.previouslyCertifiedQty), cumQty = prevQty + qty;
    const amountThisCertificate = qty * rate;
    const updatedItem = {
      variationId: row.variationId,
      variationNo: row.variationNo,
      title: row.title,
      description: row.description,
      approvedQty: numberOrZero(row.approvedQty),
      previouslyCertifiedQty: prevQty,
      quantityThisCertificate: value,
      cumulativeQty: cumQty,
      rate,
      approvedValue: numberOrZero(row.approvedValue),
      amountThisCertificate,
      paymentCertificateVoAmount: amountThisCertificate,
      balanceValue: numberOrZero(row.approvedValue) - cumQty * rate,
    };
    patchCertificateForm({
  extraWorkItems: [
    ...(cleanExtraWorkItems(safeCertificateForm.extraWorkItems || []).filter(
      (i) => i.variationId !== row.variationId
    )),
    updatedItem,
  ].sort((a, b) =>
    String(a.variationNo || "").localeCompare(String(b.variationNo || ""), undefined, {
      numeric: true,
    })
  ),
});
  };

  // ── P&G claim days handler ──

  const handlePngDaysChange = (item, value) => {
    const days = numberOrZero(value);
    const amt = days * item.rate;
    const existing = Array.isArray(safeCertificateForm.claimsItems) ? safeCertificateForm.claimsItems : [];
    const updated = { claimId: item.claimId, claimNo: item.claimNo, title: item.title, daysThisCertificate: value, amountThisCertificate: amt };
    const rest = existing.filter((ci) => ci.claimId !== item.claimId);
    patchCertificateForm({ claimsItems: [...rest, updated] });
  };

  // ── Excel import ──
  // Step 1: read the file into a raw array-of-arrays (no dependency on header
  // names). We keep every column so the user can map them. Step 2 (applyExcelImport)
  // pulls the chosen item/qty columns and runs the SOQ-matching logic.

  const handleCertificateExcelFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      setExcelImportError(""); setExcelImportMessage("");
      const arrayBuffer = await file.arrayBuffer();
      const workbook = read(arrayBuffer, { type: "array" });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      // header:1 => array-of-arrays, indexed by column position rather than name.
      const rows = utils.sheet_to_json(worksheet, { header: 1, defval: "", blankrows: false });
      const nonEmpty = rows.filter((r) => Array.isArray(r) && r.some((c) => String(c).trim() !== ""));
      if (!nonEmpty.length) { setExcelImportError("The selected Excel file does not contain any data."); return; }

      // Detect likely header row and sensible default column mapping.
      const colCount = nonEmpty.reduce((m, r) => Math.max(m, r.length), 0);
      const firstRow = nonEmpty[0].map((c) => String(c).trim().toLowerCase());
      const looksLikeHeader = firstRow.some((c) => /item|no|code|ref|qty|quant|amount|description|desc/.test(c));
      let itemCol = 0, qtyCol = colCount > 1 ? 1 : 0;
      if (looksLikeHeader) {
        const itemIdx = firstRow.findIndex((c) => /item|code|ref|bill|soq/.test(c));
        const qtyIdx = firstRow.findIndex((c) => /qty|quant/.test(c));
        if (itemIdx !== -1) itemCol = itemIdx;
        if (qtyIdx !== -1) qtyCol = qtyIdx;
      }

      setImportSheetRows(nonEmpty);
      setImportFileName(file.name);
      setImportItemCol(itemCol);
      setImportQtyCol(qtyCol);
      setImportSkipFirstRow(looksLikeHeader);
    } catch {
      setExcelImportError("Could not read the Excel file. Check it is a valid .xlsx or .xls file.");
    } finally {
      event.target.value = "";
    }
  };

  const cancelExcelImport = () => {
    setImportSheetRows(null);
    setImportFileName("");
    setExcelImportError("");
  };

  const applyExcelImport = () => {
    if (!importSheetRows) return;
    setExcelImportError(""); setExcelImportMessage("");
    if (importItemCol === importQtyCol) {
      setExcelImportError("Item number and quantity must be different columns.");
      return;
    }
    const dataRows = importSkipFirstRow ? importSheetRows.slice(1) : importSheetRows;
    const importedRows = dataRows
      .map((r) => ({
        itemNo: normalizeItemNo(r[importItemCol]),
        quantity: numberOrZero(r[importQtyCol]),
      }))
      .filter((r) => r.itemNo && r.quantity > 0);
    if (!importedRows.length) {
      setExcelImportError("No valid rows found in the selected columns. Check the column mapping — the item column should hold item numbers and the quantity column should hold numbers greater than zero.");
      return;
    }
    const importedItemMap = importedRows.reduce((acc, r) => { acc[r.itemNo] = (acc[r.itemNo] || 0) + r.quantity; return acc; }, {});
    const matchedItems = [], unmatchedItems = [];
    Object.entries(importedItemMap).forEach(([itemNo, quantity]) => {
      const vRow = valuationRows.find((r) => normalizeItemNo(r.itemNo) === normalizeItemNo(itemNo));
      if (!vRow) { unmatchedItems.push(itemNo); return; }
      const rate = numberOrZero(vRow.rate), previousQty = numberOrZero(vRow.previousQty), thisMonthQty = numberOrZero(quantity), cumulativeQty = previousQty + thisMonthQty;
      matchedItems.push({ itemNo: vRow.itemNo, description: vRow.description, unit: vRow.unit, previousQty, thisMonthQty: String(thisMonthQty), cumulativeQty, rate, amountThisMonth: (thisMonthQty * rate), amountToDate: (cumulativeQty * rate), subjectToCpa: true });
    });
    if (!matchedItems.length) { setExcelImportError(`No imported item numbers matched the SOQ register. Unmatched: ${unmatchedItems.join(", ")}`); return; }
    const matchedNos = new Set(matchedItems.map((i) => normalizeItemNo(i.itemNo)));
    const retained = (safeCertificateForm.claimedItems || []).filter((i) => !matchedNos.has(normalizeItemNo(i.itemNo)));
    patchCertificateForm({ claimedItems: [...retained, ...matchedItems].sort((a, b) => String(a.itemNo || "").localeCompare(String(b.itemNo || ""), undefined, { numeric: true })) });
    setExcelImportMessage(`Imported ${matchedItems.length} item(s).${unmatchedItems.length ? ` Unmatched: ${unmatchedItems.join(", ")}.` : ""}`);
    setImportSheetRows(null);
    setImportFileName("");
  };

  // ── W1 PDF ──

  const handleGenerateW1SummaryPdf = (selectedRow = null) => {
    const latestRow = selectedRow || effectiveRows[effectiveRows.length - 1];
    if (!latestRow) { alert("No certificates are available to generate the W1 summary."); return; }
    // Gate: a certificate still awaiting the engineer's approval must not produce a W1.
    if (latestRow.status === "awaiting_approval") {
      alert(`Certificate ${latestRow.certificateNo || latestRow.id} is awaiting your approval. Approve it (✓ Approve in the table) before generating the W1.`);
      return;
    }
    const latestIndex = Math.max(0, effectiveRows.findIndex((r) => r.id === latestRow.id));
    const latestPreviousRow = latestIndex === 0 ? null : effectiveRows[latestIndex - 1];
    const latestSubtotalA = numberOrZero(latestRow.effectiveSubtotalA || latestRow.subtotalA);

    // W1 is a "to date" summary, so Scheduled Works / VOs / CPA must be cumulative
    // up to and including the selected certificate, not only the current certificate.
    const w1RowsToDate = effectiveRows.slice(0, latestIndex + 1);

    const latestScheduledWorks = w1RowsToDate.reduce((sum, row) => {
      return sum + numberOrZero(
        row.effectiveScheduledWorks ??
        buildClaimedItemsSubtotal(row.claimedItems || [])
      );
    }, 0);

    const latestExtraWork = w1RowsToDate.reduce((sum, row) => {
      return sum + buildExtraWorkSubtotal(row.extraWorkItems || []);
    }, 0);

    const latestCpa = getCertificateInitialCpaCumulative(latestRow);
    const latestSubtotalB = (latestSubtotalA + latestCpa);
    const w1Financials = computeRowFinancials(latestRow, effectiveRows);
    const latestCertificateNo = String(latestRow.certificateNo || latestRow.id || "").trim();
    const latestCertificateMonth = String(latestRow.certificateMonth || "").trim();
    const latestRoe = (numberOrZero(latestRow.roeThisCertificate) || (typeof getRoeThisClaimForCertificate === "function" ? getRoeThisClaimForCertificate(roeRows, latestCertificateNo) : 0) || (typeof getRoeThisClaimForMonth === "function" ? getRoeThisClaimForMonth(roeRows, latestCertificateMonth) : 0));
    const latestRetention = w1Financials.retention;
    const latestRetentionBase = getRetentionBase(latestSubtotalB, latestSubtotalA, latestRoe, retentionOnCpa);
    const latestRetentionPercentage = hasManualRetentionOverride(latestRow) && latestRetentionBase
      ? latestRetention / latestRetentionBase
      : getRetentionPercentage(latestRow);
    const latestPenalties = w1Financials.penalties;
    const latestGrossAmountDue = w1Financials.grossAmountDue;
    const latestPreviousPayments = w1Financials.previousPayments;
    const latestNettAmount = w1Financials.nettAmount;
    const latestVat = w1Financials.vat;
    const latestAmountNowDue = w1Financials.amountNowDue;
    const materialsOnSite = numberOrZero(latestRow.materialsOnSite || latestRow.mos || latestRow.materialsOnSiteAmount);
    const specialMaterialsAdjustment = numberOrZero(latestRow.specialMaterialsAdjustment || latestRow.specialMaterials || latestRow.vowSpecialMaterials);
    const dayworks = numberOrZero(latestRow.dayworks || latestRow.dayworkAmount);
    const clientName = projectDetails.client || projectDetails.employer || projectDetails.owner || "CLIENT NAME";
    const department = projectDetails.department || "";
    const contractNumber = projectDetails.contractNumber || projectDetails.contractNo || "";
    const contractDescription = projectDetails.contractDescription || projectDetails.description || projectDetails.title || projectDetails.name || "";
    const contractor = projectDetails.contractor || latestRow.contractor || "";
    const certificateDate = latestRow.certificateDate || new Date().toISOString().slice(0, 10);
    const projectStartDate = projectDetails.startDate || projectDetails.commencementDate || projectDetails.contractStartDate || "";
    const periodStartDate = latestIndex === 0 ? projectStartDate : addOneDay(latestPreviousRow?.certificateDate);
    const periodEndDate = certificateDate;
    const totalSubtotal = (latestScheduledWorks + latestExtraWork + dayworks);
    const totalValueBeforeRetention = latestSubtotalB + specialMaterialsAdjustment;
    const netValueWorkDone = (totalValueBeforeRetention - latestRetention);
    const netIncludingMaterials = (netValueWorkDone + materialsOnSite + latestRoe);
    const totalAmountDueToDate = (netIncludingMaterials - latestPenalties);
    const fmt = (v) => new Intl.NumberFormat("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(numberOrZero(v));
    openHtmlWindow(`<!doctype html><html><head><title>W1 Summary of Payments to Date - ${escapeHtml(latestCertificateNo)}</title><style>@page{size:A4 landscape;margin:7mm}*{box-sizing:border-box}html,body{width:297mm;min-height:210mm}body{font-family:Arial,Helvetica,sans-serif;color:#111827;margin:0;padding:0;font-size:11px;line-height:1.2}.sheet{width:100%}.header-grid{display:grid;grid-template-columns:1fr 360px 40px;gap:12px;align-items:start;margin-bottom:8px}.top-lines{font-size:11px;line-height:1.25;text-transform:uppercase;font-weight:700}.certificate-meta{font-size:11px;line-height:1.25;margin-top:34px}.certificate-meta .row{display:grid;grid-template-columns:1fr 110px;gap:10px;align-items:center}.certificate-number{font-size:12px;font-weight:700;text-align:right;text-transform:uppercase;margin-bottom:8px}.sheet-ref{font-weight:700;text-align:right;font-size:12px}.title{text-align:center;font-size:13px;font-weight:700;margin:5px 0 6px;padding:4px;text-transform:uppercase;border:2px solid #111827}table{width:100%;border-collapse:collapse;table-layout:fixed}th,td{border:1px solid #111827;padding:3.8px 6px;vertical-align:middle;line-height:1.16}th{background:#f3f4f6;font-weight:700;text-align:center}.label{text-align:left}.center{text-align:center}.amount{text-align:right;white-space:nowrap}.bold{font-weight:700}.uppercase{text-transform:uppercase}.signatures{display:grid;grid-template-columns:1fr 1fr;margin-top:0;font-size:10px;line-height:1.22;border:2px solid #111827;page-break-inside:avoid}.signature-box{min-height:120px;padding:6px 10px;border-right:2px solid #111827;display:flex;flex-direction:column;justify-content:space-between}.signature-box:last-child{border-right:none}.certification-text{font-weight:700;text-align:center;margin-top:4px}.signature-area{width:260px;margin:10px auto 0;text-align:center}.signature-line{border-top:1px solid #111827;padding-top:3px;font-weight:700;text-transform:uppercase}.date-line{margin-top:8px;border-top:1px solid #111827;padding-top:3px}.meta{margin-top:4px;display:flex;justify-content:space-between;font-size:9px;color:#374151}.print-actions{margin:0 0 12px;padding:10px;background:#f9fafb;border:1px solid #e5e7eb;display:flex;justify-content:flex-end}.print-actions button{padding:8px 14px;border:none;border-radius:8px;background:#2563eb;color:white;font-weight:700;cursor:pointer}@media print{.print-actions{display:none}}</style></head><body><div class="print-actions"><button onclick="window.print()">Print / Save as PDF</button></div><div class="sheet"><div class="header-grid"><div class="top-lines"><div>${escapeHtml(clientName)}</div><div>${escapeHtml(department)}</div><div>${escapeHtml(contractNumber)}</div><div>${escapeHtml(contractDescription)}</div><div>${escapeHtml(contractor)}</div></div><div class="certificate-meta"><div class="certificate-number">PAYMENT CERTIFICATE NO.: ${escapeHtml(latestCertificateNo || "-")}</div><div class="row"><div>For the Period:</div><strong>${escapeHtml(formatPdfDate(periodStartDate))}</strong></div><div class="row"><div>to:</div><strong>${escapeHtml(formatPdfDate(periodEndDate))}</strong></div></div><div class="sheet-ref">W1</div></div><div class="title">SUMMARY OF PAYMENTS TO DATE</div><table><thead><tr><th style="width:60%">DETAILS OF PAYMENT</th><th style="width:12%" colspan="2">CUMULATIVE TOTALS TO DATE</th><th style="width:14%">TOTAL</th></tr><tr><th></th><th style="width:12%">From page</th><th style="width:14%">Total</th><th></th></tr></thead><tbody><tr><td class="label">Total of Scheduled Work done to date</td><td class="center">W2</td><td class="amount">${fmt(latestScheduledWorks)}</td><td class="amount">${fmt(latestScheduledWorks)}</td></tr><tr><td class="label">Total of Variations to date</td><td class="center">W3</td><td class="amount">${fmt(latestExtraWork)}</td><td class="amount">${fmt(latestExtraWork)}</td></tr><tr><td class="label">Total of Dayworks to date</td><td class="center">W4</td><td class="amount">${fmt(dayworks)}</td><td class="amount">${fmt(dayworks)}</td></tr><tr class="bold"><td class="label">SUBTOTAL</td><td></td><td class="amount">${fmt(totalSubtotal)}</td><td class="amount">${fmt(totalSubtotal)}</td></tr><tr><td class="label">Add Total Adjustment in Value of Special Materials</td><td class="center">W7</td><td class="amount">${fmt(specialMaterialsAdjustment)}</td><td class="amount">${fmt(specialMaterialsAdjustment)}</td></tr><tr><td class="label">Add Contract Price Adjustment on work done to date</td><td class="center">W8.1</td><td class="amount">${fmt(latestCpa)}</td><td class="amount">${fmt(latestCpa)}</td></tr><tr class="bold"><td class="label">TOTAL VALUE OF WORK DONE TO DATE</td><td></td><td class="amount">${fmt(totalValueBeforeRetention)}</td><td class="amount">${fmt(totalValueBeforeRetention)}</td></tr><tr><td class="label">Deduct retention of ${fmt(latestRetentionPercentage * 100)}%</td><td></td><td></td><td class="amount">${fmt(latestRetention)}</td></tr><tr class="bold"><td class="label">NET VALUE OF WORK DONE TO DATE</td><td></td><td></td><td class="amount">${fmt(netValueWorkDone)}</td></tr><tr><td class="label">Add 80% of Materials on Site</td><td class="center">W9</td><td></td><td class="amount">${fmt(materialsOnSite)}</td></tr><tr><td class="label">ROE</td><td class="center">ROE</td><td></td><td class="amount">${fmt(latestRoe)}</td></tr><tr class="bold"><td class="label">NET VALUE OF WORK DONE (INCLUDING MATERIALS ON SITE)</td><td></td><td></td><td class="amount">${fmt(netIncludingMaterials)}</td></tr><tr><td class="label">Deduct Penalties</td><td class="center">W5</td><td></td><td class="amount">${fmt(latestPenalties)}</td></tr><tr class="bold"><td class="label">TOTAL AMOUNT DUE TO DATE</td><td></td><td></td><td class="amount">${fmt(totalAmountDueToDate)}</td></tr><tr><td class="label">Deduct total of previous payments (excluding VAT)</td><td></td><td></td><td class="amount">${fmt(latestPreviousPayments)}</td></tr><tr class="bold uppercase"><td class="label">NET AMOUNT DUE FOR PAYMENT IN THIS CERTIFICATE (EXCLUDING VAT)</td><td></td><td></td><td class="amount">${fmt(latestNettAmount)}</td></tr><tr><td class="label">ADD 15% VAT</td><td></td><td></td><td class="amount">${fmt(latestVat)}</td></tr><tr class="bold uppercase"><td class="label">AMOUNT NOW DUE FOR PAYMENT (INCLUDING VAT)</td><td></td><td></td><td class="amount">${fmt(latestAmountNowDue)}</td></tr></tbody></table><div class="signatures"><div class="signature-box"><div><p>Issued in terms of section 6.10 of the General Conditions of Contract and is certified to be correct.</p><p class="certification-text">I hereby certify that only work that has been completed to the required standards has been claimed for and certified in this payment certificate in accordance with the provisions of the contract.</p></div><div class="signature-area"><div class="signature-line">EMPLOYER'S AGENT</div><div style="margin-top:8px">${escapeHtml(formatPdfDate(certificateDate))}</div><div class="date-line">Date</div></div></div><div class="signature-box"><div><p>Amount due for payment in terms of clause 6.10.4 of the General Conditions of Contract.</p></div><div class="signature-area"><div class="signature-line">EMPLOYER'S REPRESENTATIVE</div><div class="date-line">Date received by Employer's Representative</div></div></div></div><div class="meta"><div>Certificate: ${escapeHtml(latestCertificateNo || "-")}</div><div>Date: ${escapeHtml(certificateDate)}</div></div></div></body></html>`, { features: "width=1100,height=800" });
  };

  // ── Modal helpers ──

  const openViewModal = (row) => {
    setViewingRow(row);
    setModalMode("view");
    // Change 3: remember this cert as the last viewed
    setSelectedCertId(row.id);
  };

  const openAddModal = () => {
    if (typeof clearCertificateForm === "function") clearCertificateForm();
    setViewingRow(null);
    setEditSectionOpen(null);
    setModalMode("add");
  };

  const openEditModal = (row) => {
    if (typeof handleEditCertificate === "function") handleEditCertificate(row);
    setViewingRow(row);
    setEditSectionOpen(null);
    setModalMode("edit");
  };

  const closeModal = () => {
    setModalMode(null);
    setViewingRow(null);
    setEditSectionOpen(null);
    if (typeof clearCertificateForm === "function") clearCertificateForm();
    setValuationSearchText(""); setShowSelectedOnly(false);
    setExcelImportMessage(""); setExcelImportError("");
  };

  const handleSaveAndClose = () => {
    if (typeof handleAddOrUpdateCertificate === "function") handleAddOrUpdateCertificate();
    closeModal();
  };

  // ── Helper to compute financials for a single row (for view modal) ──

  const computeRowFinancials = (row, allEffectiveRows) => {
    const idx = allEffectiveRows.findIndex((r) => r.id === row.id);
    const prev = idx > 0 ? allEffectiveRows[idx - 1] : null;
    const subtotalA = numberOrZero(row.effectiveSubtotalA || row.subtotalA);
    const subtotalB = getCertificateSubtotalB({ ...row, effectiveSubtotalA: subtotalA });
    const certNo = String(row.certificateNo || row.id || "").trim();
    const certMonth = String(row.certificateMonth || "").trim();
    const roe = (numberOrZero(row.roeThisCertificate) || (typeof getRoeThisClaimForCertificate === "function" ? getRoeThisClaimForCertificate(roeRows, certNo) : 0) || (typeof getRoeThisClaimForMonth === "function" ? getRoeThisClaimForMonth(roeRows, certMonth) : 0));
    const calculatedRetentionVal = getRetentionBase(subtotalB, subtotalA, roe, retentionOnCpa) * getRetentionPercentage(row);
    const retentionVal = getEffectiveRetention(row, calculatedRetentionVal);
    const penaltiesVal = getPenaltyAmount(row);
    const grossVal = calculateGrossAmountDue({ ...row, retention: retentionVal, penalties: penaltiesVal }, subtotalB, subtotalA, roe);
    const prevPaymentsVal = prev ? (() => {
      const pA = numberOrZero(prev.effectiveSubtotalA || prev.subtotalA);
      const pB = getCertificateSubtotalB({ ...prev, effectiveSubtotalA: pA });
      const pRoe = numberOrZero(prev.roeThisCertificate);
      const pCalculatedRetention = getRetentionBase(pB, pA, pRoe, retentionOnCpa) * getRetentionPercentage(prev);
      const pRet = getEffectiveRetention(prev, pCalculatedRetention);
      const pPen = getPenaltyAmount(prev);
      return getPaidAmount(prev, calculateGrossAmountDue({ ...prev, retention: pRet, penalties: pPen }, pB, pA, pRoe));
    })() : 0;
    const nettVal = (grossVal - prevPaymentsVal);
    const vatVal = (nettVal * 0.15);
    const amountNowDueVal = (nettVal + vatVal);
    return { subtotalA, subtotalB, roe, retention: retentionVal, penalties: penaltiesVal, grossAmountDue: grossVal, previousPayments: prevPaymentsVal, nettAmount: nettVal, vat: vatVal, amountNowDue: amountNowDueVal };
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <section className="panel">

      {/* ── Section header ── */}
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Payment Certificates — {currentProjectId}</h2>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button type="button" onClick={() => handleGenerateW1SummaryPdf()} style={btn("#0f766e")}>Generate Latest W1 PDF</button>
          <button type="button" onClick={openAddModal} style={btn("#2563eb")}>+ Add Certificate</button>
        </div>
      </div>

      {/* ── Retention basis (contract data) ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14, flexWrap: "wrap", padding: "10px 14px", border: "1px solid #e5e7eb", borderRadius: 10, background: "#f8fafc" }}>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, fontSize: 13, color: "#374151", cursor: "pointer" }}>
          <input type="checkbox" checked={retentionOnCpa} onChange={(e) => persistRetentionOnCpa(e.target.checked)} />
          Retention applies to escalation (CPA)
        </label>
        <span style={{ fontSize: 12, color: "#64748b" }}>
          {retentionOnCpa
            ? "FIDIC 14.3 — retention = % of work + variations + CPA (+ROE). Certificate foots on the CPA-inclusive total."
            : "GCC convention — retention excludes CPA (% of work + variations +ROE only)."}
          {projectDetails?.retentionOnCpa === undefined ? ` Default derived from contract type (${String(currentContractType || "GCC")}).` : ""}
        </span>
      </div>

      {/* ── Change 3: Certificate filter bar ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
        <label style={{ fontWeight: 700, fontSize: 13, color: "#374151", whiteSpace: "nowrap" }}>Viewing:</label>
        <select
          value={selectedCertId || "ALL"}
          onChange={(e) => setSelectedCertId(e.target.value === "ALL" ? null : e.target.value)}
          style={{ borderRadius: 9999, border: "1.5px solid #d1d5db", padding: "6px 18px 6px 14px", fontWeight: 600, fontSize: 13, color: "#0f172a", background: "#fff", cursor: "pointer", minWidth: 220 }}
        >
          <option value="ALL">All Certificates</option>
          {effectiveRows.map((r) => (
            <option key={r.id} value={r.id}>
              {r.certificateNo || r.id}{r.certificateDate ? ` · ${r.certificateDate}` : ""}{r.certificateMonth ? ` · ${r.certificateMonth}` : ""}
            </option>
          ))}
        </select>
        {selectedCertId && selectedCertId !== "ALL" && (
          <button type="button" onClick={() => setSelectedCertId(null)} style={btnOutline({ fontSize: 12, padding: "4px 12px" })}>Show All</button>
        )}
      </div>

      {/* ── Column picker ── */}
      <div className="card" style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: showColumnPicker ? 12 : 0 }}>
          <div>
            <span style={{ fontWeight: 700, fontSize: 14 }}>Certificate Table Columns</span>
            <span style={{ fontSize: 12, color: "#6b7280", marginLeft: 10 }}>Customise which columns are visible</span>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" onClick={() => setShowColumnPicker((v) => !v)} style={btn("#475569")}>{showColumnPicker ? "Hide" : "Columns"}</button>
            <button type="button" onClick={resetCertificateColumns} style={btn("#94a3b8")}>Reset</button>
          </div>
        </div>
        {showColumnPicker && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 6 }}>
            {CERTIFICATE_TABLE_COLUMNS.map((col) => (
              <label key={col.key} style={{ display: "flex", gap: 8, alignItems: "center", padding: "7px 10px", border: "1px solid #e5e7eb", borderRadius: 9999, background: visibleCertificateColumns[col.key] ? "#f0fdfa" : "#fff", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
                <input type="checkbox" checked={Boolean(visibleCertificateColumns[col.key])} onChange={() => toggleCertificateColumn(col.key)} />
                {col.label}
              </label>
            ))}
          </div>
        )}
      </div>

      {/* ── Certificates table ── */}
      <div style={{ width: "100%", overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
        <table style={{ borderCollapse: "collapse", tableLayout: "fixed", whiteSpace: "nowrap" }}>
          <colgroup>
            {visibleCertificateColumns.certificateNo     && <col style={{ minWidth: 120 }} />}
            {visibleCertificateColumns.certificateDate   && <col style={{ minWidth: 110 }} />}
            {visibleCertificateColumns.certificateMonth  && <col style={{ minWidth: 100 }} />}
            {visibleCertificateColumns.subtotalA         && <col style={{ minWidth: 160 }} />}
            {visibleCertificateColumns.subtotalB         && <col style={{ minWidth: 160 }} />}
            {visibleCertificateColumns.scheduledWorks    && <col style={{ minWidth: 160 }} />}
            {visibleCertificateColumns.extraWork         && <col style={{ minWidth: 140 }} />}
            {visibleCertificateColumns.cpaInitial        && <col style={{ minWidth: 140 }} />}
            {visibleCertificateColumns.cpaCurrent        && <col style={{ minWidth: 140 }} />}
            {visibleCertificateColumns.cpaLock           && <col style={{ minWidth: 130 }} />}
            {visibleCertificateColumns.roe               && <col style={{ minWidth: 140 }} />}
            {visibleCertificateColumns.retention         && <col style={{ minWidth: 150 }} />}
            {visibleCertificateColumns.penalties         && <col style={{ minWidth: 130 }} />}
            {visibleCertificateColumns.grossAmountDue    && <col style={{ minWidth: 170 }} />}
            {visibleCertificateColumns.previousPayments  && <col style={{ minWidth: 170 }} />}
            {visibleCertificateColumns.nettAmount        && <col style={{ minWidth: 160 }} />}
            {visibleCertificateColumns.vat               && <col style={{ minWidth: 140 }} />}
            {visibleCertificateColumns.amountNowDue      && <col style={{ minWidth: 160 }} />}
            {visibleCertificateColumns.paidAmount        && <col style={{ minWidth: 160 }} />}
            {visibleCertificateColumns.notes             && <col style={{ minWidth: 200 }} />}
            {visibleCertificateColumns.actions           && <col style={{ minWidth: 90  }} />}
          </colgroup>
          <thead>
            <tr>
              {visibleCertificateColumns.certificateNo     && <th style={{ textAlign: "left",   padding: "10px 14px" }}>Certificate No</th>}
              {visibleCertificateColumns.certificateDate   && <th style={{ textAlign: "left",   padding: "10px 14px" }}>Date</th>}
              {visibleCertificateColumns.certificateMonth  && <th style={{ textAlign: "left",   padding: "10px 14px" }}>Month</th>}
              {visibleCertificateColumns.subtotalA         && <th style={{ textAlign: "right",  padding: "10px 14px" }}>Subtotal A</th>}
              {visibleCertificateColumns.subtotalB         && <th style={{ textAlign: "right",  padding: "10px 14px" }}>Subtotal B</th>}
              {visibleCertificateColumns.scheduledWorks    && <th style={{ textAlign: "right",  padding: "10px 14px" }}>Scheduled Works</th>}
              {visibleCertificateColumns.extraWork         && <th style={{ textAlign: "right",  padding: "10px 14px" }}>Extra Work</th>}
              {visibleCertificateColumns.cpaInitial        && <th style={{ textAlign: "right",  padding: "10px 14px" }}>CPA Initial</th>}
              {visibleCertificateColumns.cpaCurrent        && <th style={{ textAlign: "right",  padding: "10px 14px" }}>CPA Current</th>}
              {visibleCertificateColumns.cpaLock           && <th style={{ textAlign: "center", padding: "10px 14px" }}>CPA Lock</th>}
              {visibleCertificateColumns.roe               && <th style={{ textAlign: "right",  padding: "10px 14px" }}>ROE</th>}
              {visibleCertificateColumns.retention         && <th style={{ textAlign: "right",  padding: "10px 14px" }}>Retention</th>}
              {visibleCertificateColumns.penalties         && <th style={{ textAlign: "right",  padding: "10px 14px" }}>Penalties</th>}
              {visibleCertificateColumns.grossAmountDue    && <th style={{ textAlign: "right",  padding: "10px 14px" }}>Gross Amount Due</th>}
              {visibleCertificateColumns.previousPayments  && <th style={{ textAlign: "right",  padding: "10px 14px" }}>Previous Payments</th>}
              {visibleCertificateColumns.nettAmount        && <th style={{ textAlign: "right",  padding: "10px 14px" }}>Nett Amount</th>}
              {visibleCertificateColumns.vat               && <th style={{ textAlign: "right",  padding: "10px 14px" }}>VAT</th>}
              {visibleCertificateColumns.amountNowDue      && <th style={{ textAlign: "right",  padding: "10px 14px" }}>Amount Now Due</th>}
              {visibleCertificateColumns.paidAmount        && <th style={{ textAlign: "right",  padding: "10px 14px" }}>Paid Amount</th>}
              {visibleCertificateColumns.notes             && <th style={{ textAlign: "left",   padding: "10px 14px" }}>Notes</th>}
              {visibleCertificateColumns.actions           && <th style={{ textAlign: "center", padding: "10px 14px" }}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {displayedRows.length === 0 ? (
              <tr>
                <td colSpan={Math.max(1, visibleCertificateColumnCount)} style={{ textAlign: "center", color: "#6b7280", padding: 24 }}>
                  {effectiveRows.length === 0
                    ? <>No certificates captured yet. Click <strong>+ Add Certificate</strong> to begin.</>
                    : "No certificate matches the current filter."}
                </td>
              </tr>
            ) : (
              displayedRows.map((row, index) => {
                const rowIndexInAll = effectiveRows.findIndex((r) => r.id === row.id);
                const previous = rowIndexInAll > 0 ? effectiveRows[rowIndexInAll - 1] : null;
                const previousCpa = previous ? getCertificateCpaCumulative(previous) : 0;
                const previousInitialCpa = previous ? getCertificateInitialCpaCumulative(previous) : 0;
                const scheduledWorks = numberOrZero(row.effectiveScheduledWorks);
                const extraWork = buildExtraWorkSubtotal(row.extraWorkItems || []);
                const claimsWork = buildClaimsSubtotal(row.claimsItems || []);
              const previousSubtotalAForRow = numberOrZero(previous?.effectiveSubtotalA || previous?.subtotalA);

const subtotalA =
  previousSubtotalAForRow +
  scheduledWorks +
  extraWork +
  claimsWork;
                const subtotalB = subtotalA + getCertificateCpaCumulative(row);
                const cpaCurrent = getCertificateCpaThisCertificate(row, previousCpa);
                const cpaLockChecked = hasExplicitCertificateCpaLock(row);
                const cpaInitial = cpaLockChecked ? getCertificateInitialCpaThisCertificate(row, previousInitialCpa) : cpaCurrent;
                const certNo = String(row.certificateNo || row.id || "").trim();
                const certMonth = String(row.certificateMonth || "").trim();
                const roe = numberOrZero(row.effectiveRoeCumulative);
                const roeThisCert = numberOrZero(row.effectiveRoeThisCert);
                const calculatedRetentionForRow = getRetentionBase(subtotalB, subtotalA, roe, retentionOnCpa) * getRetentionPercentage(row);
                const retentionForRow = getEffectiveRetention(row, calculatedRetentionForRow);
                const retentionIsOverridden = hasManualRetentionOverride(row);
                const penaltiesForRow = getPenaltyAmount(row);
                const grossAmountDueForRow = calculateGrossAmountDue({ ...row, retention: retentionForRow, penalties: penaltiesForRow }, subtotalB, subtotalA, roe);
                const prevA = numberOrZero(previous?.effectiveSubtotalA || previous?.subtotalA);
                const prevB = previous ? getCertificateSubtotalB({ ...previous, effectiveSubtotalA: prevA }) : 0;
                const prevRoe = numberOrZero(previous?.effectiveRoeCumulative);
                const prevCalculatedRetention = previous ? getRetentionBase(prevB, prevA, prevRoe, retentionOnCpa) * getRetentionPercentage(previous) : 0;
                const prevRet = previous ? getEffectiveRetention(previous, prevCalculatedRetention) : 0;
                const prevPen = previous ? getPenaltyAmount(previous) : 0;
                const prevGross = previous ? calculateGrossAmountDue({ ...previous, retention: prevRet, penalties: prevPen }, prevB, prevA, prevRoe) : 0;
                const previousPaymentsForRow = previous ? getPaidAmount(previous, prevGross) : 0;
                const nettAmountValue = (grossAmountDueForRow - previousPaymentsForRow);
                const vatValue = (nettAmountValue * 0.15);
                const amountNowDueValue = (nettAmountValue + vatValue);
                const paidAmountValue = getPaidAmount(row, grossAmountDueForRow);
                const retentionPct = getRetentionPercentage(row) * 100;
                const cpaThisCert = numberOrZero(row.currentCpaCumulative) - previousCpa;
                const certCpaData = lookupCpaData(certNo);
                const awaitingApproval = row.status === "awaiting_approval";
                return (
                  <tr key={row.id || `${row.certificateNo}-${row.certificateDate}`} style={awaitingApproval ? { background: "#fef2f2" } : undefined} title={awaitingApproval ? "Awaiting engineer's approval" : undefined}>
                    {visibleCertificateColumns.certificateNo    && <td style={{ padding: "10px 14px", fontWeight: 700, whiteSpace: "nowrap" }}>
                      {row.certificateNo || row.id}
                      {awaitingApproval && <div style={{ marginTop: 4, display: "inline-flex", alignItems: "center", gap: 4, background: "#fee2e2", border: "1px solid #fca5a5", color: "#991b1b", borderRadius: 6, padding: "2px 6px", fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.04em" }}>⚠ Awaiting approval</div>}
                    </td>}
                    {visibleCertificateColumns.certificateDate   && <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>{row.certificateDate || "-"}</td>}
                    {visibleCertificateColumns.certificateMonth  && <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>{row.certificateMonth || "-"}</td>}

                    {visibleCertificateColumns.subtotalA && (
                      <td style={{ padding: "10px 14px", textAlign: "right", whiteSpace: "nowrap" }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                          {currency(subtotalA)}
                          <CalcTooltip title="Subtotal A" lines={[
                            { label: "Scheduled works this cert", value: currency(scheduledWorks) },
                            { label: "Extra work (VOs) this cert", value: currency(extraWork) },
                            { label: "P&G claims this cert", value: currency(claimsWork) },
                            { label: "Previous subtotal A", value: currency(numberOrZero(previous?.effectiveSubtotalA || previous?.subtotalA)) },
                            { separator: true },
                            { label: "Subtotal A (cumulative)", value: currency(subtotalA), highlight: true },
                          ]} />
                        </span>
                      </td>
                    )}

                    {visibleCertificateColumns.subtotalB && (
                      <td style={{ padding: "10px 14px", textAlign: "right", whiteSpace: "nowrap" }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                          {currency(subtotalB)}
                          <CalcTooltip title="Subtotal B — CPA Cumulative Breakdown" lines={(() => {
                            const lines = [];
                            lines.push({ label: "Subtotal A (cumulative)", value: currency(subtotalA) });
                            lines.push({ separator: true });

                            if (certCpaData && certCpaData.certHistory && certCpaData.certHistory.length > 0) {
                              // Collect all table keys seen across history
                              const allTableKeys = [];
                              const seenTableKeys = new Set();
                              certCpaData.certHistory.forEach((h) => {
                                Object.keys(h.perTable).forEach((key) => {
                                  if (!seenTableKeys.has(key)) { seenTableKeys.add(key); allTableKeys.push(key); }
                                });
                              });

                              // One section per CPA table
                              allTableKeys.forEach((tableKey) => {
                                const firstEntry = certCpaData.certHistory.find(h => h.perTable[tableKey]);
                                const tableTitle = (firstEntry?.perTable[tableKey]?.tableTitle || tableKey).replace(/^CPA:\s*/i, "");
                                lines.push({ label: tableTitle, value: "", highlight: false });

                                let tableRunning = 0;
                                certCpaData.certHistory.forEach((h) => {
                                  const td = h.perTable[tableKey];
                                  if (!td) return;
                                  const isCurrent = String(h.certNo).replace(/^0+/, "") === String(certCpaData.certificateNo).replace(/^0+/, "");
                                  const amount = isCurrent ? td.provisionalAmount : td.rulingAmount;
                                  const status = td.isFinal ? "Final" : "Prov";
                                  tableRunning = roundToCertificate(tableRunning + amount);
                                  lines.push({
                                    label: `  PC ${h.certNo}  (${status})`,
                                    value: currency(amount),
                                    indent: true,
                                  });
                                });
                                lines.push({ label: `  ${tableTitle} total`, value: currency(tableRunning), indent: true, highlight: true });
                                lines.push({ separator: true });
                              });

                              // Use the authoritative currentCpaCumulative from the row (same source as subtotalB)
                            const authCpa = getCertificateCpaCumulative(row);
                              lines.push({ label: "Total CPA cumulative", value: currency(authCpa), highlight: true });
                              lines.push({ separator: true });
                              lines.push({ label: "Subtotal B  (A + CPA)", value: currency(subtotalA + authCpa), highlight: true });
                            } else {
                            const authCpa = getCertificateCpaCumulative(row);
                              lines.push({ label: "CPA cumulative", value: currency(authCpa) });
                              lines.push({ separator: true });
                              lines.push({ label: "Subtotal B  (A + CPA)", value: currency(subtotalA + authCpa), highlight: true });
                            }
                            return lines;
                          })()} />
                        </span>
                      </td>
                    )}

                    {visibleCertificateColumns.scheduledWorks && (
                      <td style={{ padding: "10px 14px", textAlign: "right", whiteSpace: "nowrap" }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                          {currency(scheduledWorks)}
                          <CalcTooltip title="Scheduled Works" lines={[
                            { label: "Sum of SOQ claimed items × rate", value: currency(scheduledWorks) },
                            { label: "Items claimed", value: String((row.claimedItems || []).length) },
                            { separator: true },
                            { label: "Total this cert", value: currency(scheduledWorks), highlight: true },
                          ]} />
                        </span>
                      </td>
                    )}

                    {visibleCertificateColumns.extraWork && (
                      <td style={{ padding: "10px 14px", textAlign: "right", whiteSpace: "nowrap" }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                          {currency(extraWork)}
                          <CalcTooltip title="Extra Work (VOs)" lines={[
                            { label: "Sum of VO qty × rate this cert", value: currency(extraWork) },
                            { label: "VOs included", value: String((row.extraWorkItems || []).length) },
                            { separator: true },
                            { label: "Total extra work", value: currency(extraWork), highlight: true },
                          ]} />
                        </span>
                      </td>
                    )}

                    {visibleCertificateColumns.cpaInitial && (
                      <td style={{ padding: "10px 14px", textAlign: "right", whiteSpace: "nowrap" }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                          {currency(cpaInitial)}
                          <CalcTooltip title="CPA Initial (this cert)" lines={[
                            { label: "Locked CPA this cert", value: currency(cpaLockChecked ? numberOrZero(row.lockedCpaThisCertificate) : 0) },
                            { label: "Previous initial CPA cumulative", value: currency(previousInitialCpa) },
                            { separator: true },
                            { label: "CPA Initial", value: currency(cpaInitial), highlight: true },
                          ]} />
                        </span>
                      </td>
                    )}

                    {visibleCertificateColumns.cpaCurrent && (
                      <td style={{ padding: "10px 14px", textAlign: "right", whiteSpace: "nowrap" }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                          {currency(cpaCurrent)}
                          <CalcTooltip title="CPA Current (this cert)" lines={[
                            { label: "CPA cumulative (current)", value: currency(numberOrZero(row.currentCpaCumulative)) },
                            { label: "Less previous CPA cumulative", value: currency(previousCpa), negative: true },
                            { separator: true },
                            { label: "CPA this cert", value: currency(cpaCurrent), highlight: true },
                          ]} />
                        </span>
                      </td>
                    )}

                    {visibleCertificateColumns.cpaLock && (
                      <td style={{ padding: "10px 14px", textAlign: "center", whiteSpace: "nowrap" }}>
                        <label style={{ display: "inline-flex", gap: 6, alignItems: "center", fontWeight: 700, cursor: "pointer" }}>
                          <input type="checkbox" checked={cpaLockChecked} onChange={(e) => handleToggleCpaLock(row, e.target.checked, getCertificateCpaCumulative(row), cpaCurrent)} />
                          {getCertificateCpaLockLabel(row, latestCpaIndexStatus)}
                        </label>
                      </td>
                    )}

                    {visibleCertificateColumns.roe && (
                      <td style={{ padding: "10px 14px", textAlign: "right", whiteSpace: "nowrap" }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                          {currency(roe)}
                          <CalcTooltip title="ROE (Cumulative)" lines={[
                            { label: "ROE this cert", value: currency(roeThisCert) },
                            { label: "ROE previous certs (cum.)", value: currency(roe - roeThisCert) },
                            { separator: true },
                            { label: "ROE cumulative", value: currency(roe), highlight: true },
                          ]} />
                        </span>
                      </td>
                    )}

                    {visibleCertificateColumns.retention && (
                      <td style={{
                        padding: "10px 14px",
                        textAlign: "right",
                        whiteSpace: "nowrap",
                        color: retentionIsOverridden ? "#991b1b" : "#dc2626",
                        background: retentionIsOverridden ? "#fef2f2" : undefined,
                        boxShadow: retentionIsOverridden ? "inset 0 0 0 2px #ef4444" : undefined,
                      }}>
                        <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "flex-end", gap: 6, flexWrap: "wrap" }}>
                          {retentionIsOverridden && (
                            <span style={{ background: "#dc2626", color: "#fff", borderRadius: 9999, padding: "2px 7px", fontSize: 9, fontWeight: 800, letterSpacing: "0.04em" }}>
                              OVERRIDE
                            </span>
                          )}
                          <strong>{currency(retentionForRow)}</strong>
                          <CalcTooltip title="Retention" lines={[
                            { label: retentionOnCpa ? "Retention base (SubB + ROE, incl CPA)" : "Retention base (SubA + ROE, excl CPA)", value: currency(getRetentionBase(subtotalB, subtotalA, roe, retentionOnCpa)) },
                            { label: `Rate`, value: `${retentionPct.toFixed(1)}%` },
                            ...(retentionIsOverridden ? [
                              { label: "Calculated retention", value: currency(calculatedRetentionForRow) },
                              { label: "Manual override", value: currency(retentionForRow), negative: true },
                            ] : []),
                            { separator: true },
                            { label: "Retention deducted", value: currency(retentionForRow), highlight: true, negative: true },
                          ]} />
                        </span>
                      </td>
                    )}

                    {visibleCertificateColumns.penalties && (
                      <td style={{ padding: "10px 14px", textAlign: "right", whiteSpace: "nowrap", color: penaltiesForRow > 0 ? "#dc2626" : undefined }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                          {currency(penaltiesForRow)}
                          <CalcTooltip title="Penalties" lines={[
                            { label: "Rate per day", value: currency(numberOrZero(row.penaltiesRatePerDay)) },
                            { label: "Days", value: String(numberOrZero(row.penaltiesDays)) },
                            { separator: true },
                            { label: "Penalties", value: currency(penaltiesForRow), highlight: true, negative: penaltiesForRow > 0 },
                          ]} />
                        </span>
                      </td>
                    )}

                    {visibleCertificateColumns.grossAmountDue && (
                      <td style={{ padding: "10px 14px", textAlign: "right", whiteSpace: "nowrap" }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                          {currency(grossAmountDueForRow)}
                          <CalcTooltip title="Gross Amount Due" lines={[
                            { label: "Subtotal B", value: currency(subtotalB) },
                            { label: "Add ROE (cumulative)", value: currency(roe) },
                            { label: "Less retention", value: currency(retentionForRow), negative: true },
                            { label: "Less penalties", value: currency(penaltiesForRow), negative: true },
                            { separator: true },
                            { label: "Gross Amount Due", value: currency(grossAmountDueForRow), highlight: true },
                          ]} />
                        </span>
                      </td>
                    )}

                    {visibleCertificateColumns.previousPayments && (
                      <td style={{ padding: "10px 14px", textAlign: "right", whiteSpace: "nowrap" }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                          {currency(previousPaymentsForRow)}
                          <CalcTooltip title="Previous Payments" lines={[
                            { label: "Previous cert gross amount due", value: currency(prevGross) },
                            { label: "Previous cert paid amount", value: currency(previousPaymentsForRow) },
                            { label: "Previous cert", value: previous ? (previous.certificateNo || previous.id || "—") : "None" },
                            { separator: true },
                            { label: "Previous payments deducted", value: currency(previousPaymentsForRow), highlight: true, negative: true },
                          ]} />
                        </span>
                      </td>
                    )}

                    {visibleCertificateColumns.nettAmount && (
                      <td style={{ padding: "10px 14px", textAlign: "right", whiteSpace: "nowrap" }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                          {currency(nettAmountValue)}
                          <CalcTooltip title="Nett Amount" lines={[
                            { label: "Gross Amount Due", value: currency(grossAmountDueForRow) },
                            { label: "Less previous payments", value: currency(previousPaymentsForRow), negative: true },
                            { separator: true },
                            { label: "Nett Amount", value: currency(nettAmountValue), highlight: true },
                          ]} />
                        </span>
                      </td>
                    )}

                    {visibleCertificateColumns.vat && (
                      <td style={{ padding: "10px 14px", textAlign: "right", whiteSpace: "nowrap" }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                          {currency(vatValue)}
                          <CalcTooltip title="VAT" lines={[
                            { label: "Nett Amount", value: currency(nettAmountValue) },
                            { label: "VAT rate", value: "15%" },
                            { separator: true },
                            { label: "VAT", value: currency(vatValue), highlight: true },
                          ]} />
                        </span>
                      </td>
                    )}

                    {visibleCertificateColumns.amountNowDue && (
                      <td style={{ padding: "10px 14px", textAlign: "right", whiteSpace: "nowrap", fontWeight: 700, color: "#0d9488" }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                          {currency(amountNowDueValue)}
                          <CalcTooltip title="Amount Now Due" lines={[
                            { label: "Nett Amount (excl. VAT)", value: currency(nettAmountValue) },
                            { label: "VAT (15%)", value: currency(vatValue) },
                            { separator: true },
                            { label: "Amount Now Due (incl. VAT)", value: currency(amountNowDueValue), highlight: true },
                          ]} />
                        </span>
                      </td>
                    )}

                    {visibleCertificateColumns.paidAmount && (
                      <td style={{ padding: "10px 14px", textAlign: "right", whiteSpace: "nowrap" }}>
                        <label style={{ display: "inline-flex", gap: 8, alignItems: "center", fontWeight: 700, cursor: "pointer" }}>
                          <input type="checkbox" checked={isCertificatePaid(row)} onChange={(e) => { if (typeof handleUpdateCertificatePayment === "function") handleUpdateCertificatePayment(row, { isPaid: e.target.checked, paidAmount: e.target.checked ? grossAmountDueForRow : 0 }); }} />
                          {paidAmountValue > 0 ? currency(paidAmountValue) : "-"}
                        </label>
                      </td>
                    )}

                    {visibleCertificateColumns.notes             && <td style={{ padding: "10px 14px", whiteSpace: "normal", maxWidth: 260 }}>{row.notes || "-"}</td>}
                    {visibleCertificateColumns.actions           && (
                      <td style={{ padding: "10px 14px", textAlign: "center", whiteSpace: "nowrap" }}>
                        {awaitingApproval && typeof handleSetCertificateApproval === "function" && (
                          <button type="button" onClick={() => { if (window.confirm(`Approve certificate ${row.certificateNo || row.id}? This confirms the captured figures and CPA are correct.`)) handleSetCertificateApproval(row, true); }} style={btn("#16a34a")}>✓ Approve</button>
                        )}
                        {!awaitingApproval && row.status === "approved" && typeof handleSetCertificateApproval === "function" && (
                          <button type="button" onClick={() => handleSetCertificateApproval(row, false)} style={btn("#b45309")} title="Revert to awaiting approval">Unapprove</button>
                        )}
                        <button type="button" onClick={() => openViewModal(row)} style={btn("#059669")}>View</button>
                        <button type="button" onClick={() => setHistoryRow(row)} style={btn("#475569")}>History</button>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          CHANGE 1 — VIEW MODAL (read-only, with action buttons in header)
      ═══════════════════════════════════════════════════════════════════════ */}
      {modalMode === "view" && viewingRow && (() => {
        const vRow = viewingRow;
        const fin = computeRowFinancials(vRow, effectiveRows);
        return (
          <CertificateModal
            title={`Payment Certificate ${vRow.certificateNo || vRow.id || ""}`}
            subtitle={`${vRow.certificateDate || ""}${vRow.certificateMonth ? " · " + vRow.certificateMonth : ""}`}
            onClose={closeModal}
            headerActions={
              <>
                <button type="button" onClick={() => handleGenerateW1SummaryPdf(vRow)} style={btn("#0f766e")}>W1 PDF</button>
                <button type="button" onClick={() => { closeModal(); setTimeout(() => openEditModal(vRow), 10); }} style={btn("#2563eb")}>Edit</button>
                {typeof handleDeleteCertificate === "function" && (
                  <button type="button" onClick={() => { if (window.confirm(`Delete Certificate ${vRow.certificateNo || vRow.id}?`)) { handleDeleteCertificate(vRow.id); closeModal(); } }} style={btn("#dc2626")}>Delete</button>
                )}
              </>
            }
          >
            {hasManualRetentionOverride(vRow) && (
              <div style={{ background: "#fef2f2", border: "2px solid #ef4444", borderRadius: 10, padding: "10px 14px", marginBottom: 12, color: "#991b1b", fontWeight: 700, fontSize: 13 }}>
                Retention was manually overridden to {currency(fin.retention)}. The automatic figure was {currency(getRetentionBase(fin.subtotalB, fin.subtotalA, fin.roe, retentionOnCpa) * getRetentionPercentage(vRow))}.
              </div>
            )}
            {/* Summary pills */}
            <SummaryPills items={[
              { label: "Retention", value: currency(fin.retention), accent: "#dc2626" },
              { label: "Penalties", value: currency(fin.penalties), accent: fin.penalties > 0 ? "#dc2626" : undefined },
              { label: "Gross Amount Due", value: currency(fin.grossAmountDue) },
              { label: "Previous Payments", value: currency(fin.previousPayments) },
              { label: "Nett Amount", value: currency(fin.nettAmount), accent: "#0891b2" },
              { label: "VAT (15%)", value: currency(fin.vat) },
              { label: "Amount Now Due", value: currency(fin.amountNowDue), accent: "#0d9488" },
            ]} />

            {/* Detail fields */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginTop: 8 }}>
              {[
                ["Certificate No", vRow.certificateNo || vRow.id || "-"],
                ["Certificate Date", vRow.certificateDate || "-"],
                ["Certificate Month", vRow.certificateMonth || "-"],
                ["Subtotal A", currency(fin.subtotalA)],
                ["Subtotal B", currency(fin.subtotalB)],
                ["ROE", currency(fin.roe)],
                ["Retention %", `${(getRetentionPercentage(vRow) * 100).toFixed(2)}%`],
                ["Penalties Rate/Day", vRow.penaltiesRatePerDay ? currency(vRow.penaltiesRatePerDay) : "-"],
                ["Penalty Days", vRow.penaltiesDays || "-"],
                ["CPA Lock", getCertificateCpaLockLabel(vRow, latestCpaIndexStatus)],
                ["Scheduled Works", currency(numberOrZero(vRow.effectiveScheduledWorks))],
                ["Extra Work", currency(buildExtraWorkSubtotal(vRow.extraWorkItems || []))],
              ].map(([label, value]) => (
                <div key={label} style={{ background: "#f8fafc", borderRadius: 10, padding: "10px 14px", border: "1px solid #e5e7eb" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>{label}</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#0f172a" }}>{value}</div>
                </div>
              ))}
            </div>

            {vRow.notes && (
              <div style={{ marginTop: 14, background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10, padding: "10px 14px" }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#92400e", textTransform: "uppercase", marginBottom: 4 }}>Notes</div>
                <div style={{ fontSize: 13, color: "#0f172a" }}>{vRow.notes}</div>
              </div>
            )}

            {/* Claimed items summary (read-only) */}
            {(vRow.claimedItems || []).length > 0 && (
              <div style={{ marginTop: 20 }}>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10, color: "#0891b2", display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ display: "inline-block", width: 4, height: 18, background: "#0891b2", borderRadius: 2 }} />
                  Monthly Valuation — {(vRow.claimedItems || []).length} items
                </div>
                <div style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: 10, boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                  <table style={{ ...proTableStyle, minWidth: 720 }}>
                    <thead>
                      <tr>
                        <th style={proTh({ textAlign: "left", width: 70 })}>Item No</th>
                        <th style={proTh({ textAlign: "left", minWidth: 180, maxWidth: 260, whiteSpace: "normal" })}>Description</th>
                        <th style={proTh({ textAlign: "center", width: 55 })}>Unit</th>
                        <th style={proTh({ textAlign: "right", width: 90 })}>Prev Qty</th>
                        <th style={proTh({ textAlign: "right", width: 110, color: "#0891b2" })}>This Month Qty</th>
                        <th style={proTh({ textAlign: "right", width: 90 })}>Cum Qty</th>
                        <th style={proTh({ textAlign: "right", width: 120 })}>Rate</th>
                        <th style={proTh({ textAlign: "right", width: 130 })}>This Month Amt</th>
                        <th style={proTh({ textAlign: "right", width: 130, borderRight: "none" })}>Amt to Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(vRow.claimedItems || []).map((item, idx) => (
                        <tr key={item.itemNo} style={{ background: idx % 2 === 0 ? "#fff" : "#f8fafc" }}>
                          <td style={proTd({ fontWeight: 600 })}>{item.itemNo}</td>
                          <td style={proTd({ whiteSpace: "normal", maxWidth: 260, fontSize: 12, lineHeight: 1.4 })}>{item.description || "-"}</td>
                          <td style={proTd({ textAlign: "center", color: "#6b7280" })}>{item.unit || "-"}</td>
                          <td style={proTd({ textAlign: "right" })}>{fmt2(item.previousQty)}</td>
                          <td style={proTd({ textAlign: "right", fontWeight: 600, color: "#0891b2" })}>{fmt2(item.thisMonthQty)}</td>
                          <td style={proTd({ textAlign: "right" })}>{fmt2(item.cumulativeQty)}</td>
                          <td style={proTd({ textAlign: "right" })}>{currency(item.rate)}</td>
                          <td style={proTd({ textAlign: "right", fontWeight: 600 })}>{currency(item.amountThisMonth)}</td>
                          <td style={proTd({ textAlign: "right", borderRight: "none" })}>{currency(item.amountToDate)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={7} style={proTfoot({ textAlign: "right", color: "#6b7280", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em" })}>Total This Certificate</td>
                        <td style={proTfoot({ textAlign: "right" })}>{currency(buildClaimedItemsSubtotal(vRow.claimedItems || []))}</td>
                        <td style={proTfoot({ borderRight: "none" })} />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}

            {/* Extra work summary (read-only) */}
            {(vRow.extraWorkItems || []).length > 0 && (
              <div style={{ marginTop: 20 }}>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10, color: "#7c3aed", display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ display: "inline-block", width: 4, height: 18, background: "#7c3aed", borderRadius: 2 }} />
                  Approved VOs / Extra Work — {(vRow.extraWorkItems || []).length} items
                </div>
                <div style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: 10, boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                  <table style={{ ...proTableStyle, minWidth: 760 }}>
                    <thead>
                      <tr>
                        <th style={proTh({ textAlign: "left", width: 60 })}>VO No</th>
                        <th style={proTh({ textAlign: "left", minWidth: 160, maxWidth: 220, whiteSpace: "normal" })}>Title</th>
                        <th style={proTh({ textAlign: "right", width: 90 })}>Approved Qty</th>
                        <th style={proTh({ textAlign: "right", width: 90 })}>Prev Qty</th>
                        <th style={proTh({ textAlign: "right", width: 100 })}>This Cert Qty</th>
                        <th style={proTh({ textAlign: "right", width: 90 })}>Cum Qty</th>
                        <th style={proTh({ textAlign: "right", width: 120 })}>Rate</th>
                        <th style={proTh({ textAlign: "right", width: 130 })}>Amt This Cert</th>
                        <th style={proTh({ textAlign: "right", width: 120, borderRight: "none" })}>Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(vRow.extraWorkItems || []).map((item, idx) => (
                        <tr key={item.variationId} style={{ background: idx % 2 === 0 ? "#fff" : "#f8fafc" }}>
                          <td style={proTd({ fontWeight: 600 })}>{item.variationNo || "-"}</td>
                          <td style={proTd({ whiteSpace: "normal", maxWidth: 220, fontSize: 12, lineHeight: 1.4 })}>{item.title || item.description || "-"}</td>
                          <td style={proTd({ textAlign: "right" })}>{fmt2(item.approvedQty)}</td>
                          <td style={proTd({ textAlign: "right" })}>{fmt2(item.previouslyCertifiedQty)}</td>
                          <td style={proTd({ textAlign: "right", fontWeight: 600, color: "#7c3aed" })}>{fmt2(item.quantityThisCertificate)}</td>
                          <td style={proTd({ textAlign: "right" })}>{fmt2(item.cumulativeQty)}</td>
                          <td style={proTd({ textAlign: "right" })}>{currency(item.rate)}</td>
                          <td style={proTd({ textAlign: "right", fontWeight: 600 })}>{currency(item.amountThisCertificate)}</td>
                          <td style={proTd({ textAlign: "right", color: numberOrZero(item.balanceValue) < 0 ? "#dc2626" : "#0f172a", borderRight: "none" })}>{currency(item.balanceValue)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={7} style={proTfoot({ textAlign: "right", color: "#6b7280", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em" })}>Total Extra Work</td>
                        <td style={proTfoot({ textAlign: "right" })}>{currency(buildExtraWorkSubtotal(vRow.extraWorkItems || []))}</td>
                        <td style={proTfoot({ borderRight: "none" })} />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </CertificateModal>
        );
      })()}

      {/* ══════════════════════════════════════════════════════════════════════
          EDIT / ADD MODAL
      ═══════════════════════════════════════════════════════════════════════ */}
      {(modalMode === "edit" || modalMode === "add") && (
        <CertificateModal
          title={modalMode === "edit" ? "Generate Payment Certificate" : "Generate Payment Certificate"}
          subtitle={modalMode === "edit"
            ? `${safeCertificateForm.certificateNo || ""} · ${safeCertificateForm.certificateDate || ""} · ${safeCertificateForm.certificateMonth || ""}`
            : "New certificate — fill in the details below"}
          onClose={closeModal}
          headerActions={
            <button type="button" onClick={handleSaveAndClose} style={btn("#0d9488")}>
              {modalMode === "edit" ? "Update Certificate" : "Save Certificate"}
            </button>
          }
        >
          {/* Editing banner */}
          {modalMode === "edit" && (
            <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 10, padding: "10px 14px", marginBottom: 16, color: "#1d4ed8", fontWeight: 700, fontSize: 14 }}>
              Editing Payment Certificate {safeCertificateForm.certificateNo || ""}
            </div>
          )}

          {/* Basic fields */}
          <CertificateFormRow label="Certificate Number" helper="e.g. PC-01 or Certificate 5">
            <input type="text" value={safeCertificateForm.certificateNo || ""} onChange={(e) => patchCertificateForm({ certificateNo: e.target.value })} />
          </CertificateFormRow>
          <CertificateFormRow label="Certificate Date" helper="The actual date on the certificate">
            <input type="date" value={safeCertificateForm.certificateDate || ""} onChange={(e) => patchCertificateForm({ certificateDate: e.target.value })} />
          </CertificateFormRow>
          <CertificateFormRow label="Certificate Month" helper="Select the cashflow month this certificate maps to">
            <select value={safeCertificateForm.certificateMonth || ""} onChange={(e) => patchCertificateForm({ certificateMonth: e.target.value })}>
              <option value="">Select certificate month</option>
              {availableMonths.map((month) => {
                const val = typeof month === "string" ? month : month?.value || month?.key || "";
                const lbl = typeof month === "string" ? month : month?.label || month?.value || month?.key || "";
                return <option key={val} value={val}>{lbl}</option>;
              })}
            </select>
          </CertificateFormRow>
          <CertificateFormRow label="Retention Percentage" helper="Default 5%">
            <input type="number" step="0.01" value={retentionPercentage * 100} onChange={(e) => patchCertificateForm({ retentionPercentage: String(numberOrZero(e.target.value) / 100) })} />
          </CertificateFormRow>
          <CertificateFormRow
            label="Retention Amount"
            helper={retentionIsManuallyOverridden
              ? `Manual override active. Automatic calculation: ${currency(calculatedRetention)}`
              : "Automatically calculated. Edit this figure to override it for this certificate."}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <input
                type="number"
                step="0.01"
                value={retentionIsManuallyOverridden ? safeCertificateForm.retentionOverride : display2dp(calculatedRetention)}
                onChange={(e) => patchCertificateForm({ retentionOverride: e.target.value })}
                aria-label="Retention amount"
                style={{
                  flex: "1 1 220px",
                  color: retentionIsManuallyOverridden ? "#991b1b" : undefined,
                  background: retentionIsManuallyOverridden ? "#fef2f2" : undefined,
                  border: retentionIsManuallyOverridden ? "2px solid #ef4444" : undefined,
                  fontWeight: retentionIsManuallyOverridden ? 800 : undefined,
                }}
              />
              {retentionIsManuallyOverridden && (
                <>
                  <span aria-live="polite" style={{ background: "#dc2626", color: "#fff", borderRadius: 9999, padding: "4px 9px", fontSize: 10, fontWeight: 800, letterSpacing: "0.04em" }}>
                    MANUAL OVERRIDE
                  </span>
                  <button type="button" onClick={() => patchCertificateForm({ retentionOverride: "" })} style={btnOutline({ color: "#991b1b", borderColor: "#ef4444", padding: "5px 12px", fontSize: 11 })}>
                    Use Automatic
                  </button>
                </>
              )}
            </div>
          </CertificateFormRow>
          <CertificateFormRow label="Penalties Rate Per Day" helper="Contractual penalty rate per calendar day, excluding VAT">
            <input type="number" step="0.01" value={safeCertificateForm.penaltiesRatePerDay || ""} onChange={(e) => patchCertificateForm({ penaltiesRatePerDay: e.target.value })} />
          </CertificateFormRow>
          <CertificateFormRow label="Penalty Days" helper="Number of penalty days applicable to this certificate">
            <input type="number" step="1" value={safeCertificateForm.penaltiesDays || ""} onChange={(e) => patchCertificateForm({ penaltiesDays: e.target.value })} />
          </CertificateFormRow>
          <CertificateFormRow label="Notes" helper="Optional certificate remarks">
            <textarea value={safeCertificateForm.notes || ""} onChange={(e) => patchCertificateForm({ notes: e.target.value })} rows={3} style={{ width: "100%", resize: "vertical" }} />
          </CertificateFormRow>

          {/* Calculated summary pills */}
          <SummaryPills items={[
            { label: "Retention", value: currency(retention), accent: "#dc2626" },
            { label: "Penalties", value: currency(penalties), accent: penalties > 0 ? "#dc2626" : undefined },
            { label: "Gross Amount Due", value: currency(grossAmountDue) },
            { label: "Previous Payments", value: currency(previousPayments) },
            { label: "Nett Amount", value: currency(nettAmount), accent: "#0891b2" },
            { label: "VAT (15%)", value: currency(vat) },
            { label: "Amount Now Due", value: currency(amountNowDue), accent: "#0d9488" },
          ]} />

          {/* Show Calculated Fields */}
          <details style={{ marginBottom: 16 }}>
            <summary style={{ fontWeight: 700, fontSize: 13, cursor: "pointer", color: "#475569", userSelect: "none", padding: "6px 0" }}>Show Calculated Fields</summary>
            <div style={{ marginTop: 12, paddingLeft: 4 }}>
              <CertificateFormRow label="Subtotal A (Cumulative)" helper="SOQ claimed + approved VOs + P&G claims"><input type="text" value={currency(subtotalACumulative)} readOnly /></CertificateFormRow>
              <CertificateFormRow label="Previous Subtotal A" helper="From previous certificate"><input type="text" value={currency(previousSubtotalA)} readOnly /></CertificateFormRow>
              <CertificateFormRow label="Scheduled Works This Cert" helper="From SOQ claimed quantities"><input type="text" value={currency(scheduledWorksThisCertificate)} readOnly /></CertificateFormRow>
              <CertificateFormRow label="Extra Work / Approved VOs" helper="From approved VOs selected below"><input type="text" value={currency(extraWorkThisCertificate)} readOnly /></CertificateFormRow>
              <CertificateFormRow label="P&G Claims This Cert" helper="From approved P&G claims — days × rate"><input type="text" value={currency(claimsThisCertificate)} readOnly /></CertificateFormRow>
              <CertificateFormRow label="Subtotal B (Cumulative)" helper="Subtotal A + cumulative CPA"><input type="text" value={currency(subtotalBCumulative)} readOnly /></CertificateFormRow>
              <CertificateFormRow label="Previous Subtotal B" helper="From previous certificate"><input type="text" value={currency(previousSubtotalB)} readOnly /></CertificateFormRow>
              <CertificateFormRow label="CPA This Certificate" helper={hasLockedCpaCumulative ? "Locked CPA snapshot." : "Draft — live CPA value."}><input type="text" value={currency(cpaThisCertificate)} readOnly /></CertificateFormRow>
              <CertificateFormRow label="CPA Lock Status" helper={`Contract: ${currentContractType}. ${cpaIndexStatusLabel}`}><input type="text" value={hasLockedCpaCumulative ? `Locked (${cpaIndexStatusLabel})` : `Draft live (${cpaIndexStatusLabel})`} readOnly /></CertificateFormRow>
              <CertificateFormRow label="ROE This Certificate" helper="From ROE section — by cert no then month"><input type="text" value={currency(roeThisCertificate)} readOnly /></CertificateFormRow>
            </div>
          </details>

          {/* ── Change 2: Section toggle buttons side by side ── */}
          <div style={{ borderTop: "2px solid #e5e7eb", paddingTop: 16, marginTop: 4 }}>
            <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
              <SectionToggleBtn
                label="Monthly Valuation (Claimed Quantities)"
                count={(safeCertificateForm.claimedItems || []).length}
                amount={currency(scheduledWorksThisCertificate)}
                active={editSectionOpen === "valuation"}
                color="#0891b2"
                onClick={() => setEditSectionOpen((s) => s === "valuation" ? null : "valuation")}
              />
              <SectionToggleBtn
                label="Approved VOs / Extra Work"
                count={validExtraWorkItems.length}
                amount={currency(extraWorkThisCertificate)}
                active={editSectionOpen === "vos"}
                color="#7c3aed"
                onClick={() => setEditSectionOpen((s) => s === "vos" ? null : "vos")}
              />
              <SectionToggleBtn
                label="Approved P&G Claims"
                count={approvedPngItems.length}
                amount={currency(approvedPngTotal)}
                active={editSectionOpen === "png"}
                color="#b45309"
                onClick={() => setEditSectionOpen((s) => s === "png" ? null : "png")}
              />
            </div>

            {/* Monthly Valuation expanded */}
            {editSectionOpen === "valuation" && (
              <div style={{ border: "2px solid #0891b2", borderRadius: 12, padding: 14, marginBottom: 14 }}>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
                  <input type="text" placeholder="Search by item number or description…" value={valuationSearchText} onChange={(e) => setValuationSearchText(e.target.value)} style={{ flex: "1 1 220px", minWidth: 0, borderRadius: 9999 }} />
                  <SectionFilter items={valuationRows} value={valuationSectionFilter} onChange={setValuationSectionFilter} />
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 700, whiteSpace: "nowrap" }}>
                    <input type="checkbox" checked={showSelectedOnly} onChange={(e) => setShowSelectedOnly(e.target.checked)} /> Selected only
                  </label>
                  <input ref={fileInputRef} type="file" accept=".xlsx,.xls" style={{ display: "none" }} onChange={handleCertificateExcelFile} />
                  <button type="button" onClick={() => setShowImportHelper(true)} style={btn("#475569")}>Import from Excel</button>
                  <input ref={contractorFileRef} type="file" accept=".xlsx,.xls,.csv,.pdf,image/*" style={{ display: "none" }} onChange={(e) => handleContractorSubmission(e.target.files?.[0])} />
                  <button type="button" onClick={() => contractorFileRef.current?.click()} disabled={scanBusy} title="AI-read a contractor's monthly claim/measurement sheet (Excel, PDF or photo) and pre-fill the valuation" style={btn(scanBusy ? "#94a3b8" : "#7c3aed")}>
                    {scanBusy ? "Reading…" : "⤓ Capture from contractor submission"}
                  </button>
                </div>

                {scanMsg && (
                  <div style={{
                    margin: "0 0 12px", padding: "10px 12px", borderRadius: 8, fontSize: 13, fontWeight: 600, lineHeight: 1.4,
                    ...(scanMsg.type === "ok"    ? { background: "#f0fdf4", border: "1px solid #86efac", color: "#14532d" } : {}),
                    ...(scanMsg.type === "warn"  ? { background: "#fff7ed", border: "1px solid #fdba74", color: "#92400e" } : {}),
                    ...(scanMsg.type === "error" ? { background: "#fef2f2", border: "1px solid #fca5a5", color: "#991b1b" } : {}),
                    ...(scanMsg.type === "info"  ? { background: "#eff6ff", border: "1px solid #bfdbfe", color: "#1e40af" } : {}),
                  }}>{scanMsg.text}</div>
                )}

                {/* Pre-upload helper popup */}
                {showImportHelper && (
                  <div style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(15,23,42,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} onClick={() => setShowImportHelper(false)}>
                    <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, maxWidth: 460, width: "100%", padding: "24px 26px", boxShadow: "0 24px 60px rgba(15,23,42,0.3)", border: "1px solid #e5e7eb" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                        <div style={{ width: 36, height: 36, borderRadius: 9, background: "#eff6ff", color: "#2563eb", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>↑</div>
                        <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: "#0f172a" }}>Before you import</h3>
                      </div>
                      <p style={{ margin: "0 0 12px", fontSize: 13.5, color: "#475569", lineHeight: 1.5 }}>
                        Please ensure your Excel sheet has <strong>two columns of data only</strong>:
                      </p>
                      <ul style={{ margin: "0 0 12px", padding: "0 0 0 18px", fontSize: 13.5, color: "#334155", lineHeight: 1.7 }}>
                        <li><strong>Column 1</strong> — item numbers only</li>
                        <li><strong>Column 2</strong> — quantities only</li>
                      </ul>
                      <p style={{ margin: "0 0 18px", fontSize: 12.5, color: "#64748b", lineHeight: 1.5 }}>
                        After you select the file you'll be able to confirm which column is which, so it's fine if your sheet has a header row or a slightly different order.
                      </p>
                      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                        <button type="button" onClick={() => setShowImportHelper(false)} style={btnOutline()}>Cancel</button>
                        <button type="button" onClick={() => { setShowImportHelper(false); fileInputRef.current?.click(); }} style={btn("#2563eb")}>Choose file…</button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Column mapping panel (after a file is loaded) */}
                {importSheetRows && (() => {
                  const colCount = importSheetRows.reduce((m, r) => Math.max(m, r.length), 0);
                  const colOptions = Array.from({ length: colCount }, (_, i) => i);
                  const colLabel = (i) => `Column ${String.fromCharCode(65 + i)}`;
                  const previewBody = (importSkipFirstRow ? importSheetRows.slice(1) : importSheetRows).slice(0, 5);
                  const sameCol = importItemCol === importQtyCol;
                  const selectStyle = { padding: "6px 10px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 13, fontWeight: 600, background: "#fff", minWidth: 130 };
                  return (
                    <div style={{ border: "2px solid #2563eb", borderRadius: 12, padding: 16, marginBottom: 12, background: "#f8fafc" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
                        <div style={{ fontWeight: 800, fontSize: 14, color: "#0f172a" }}>Map columns <span style={{ fontWeight: 500, color: "#64748b", fontSize: 12 }}>· {importFileName}</span></div>
                        <button type="button" onClick={cancelExcelImport} style={{ ...btnOutline(), padding: "4px 12px", fontSize: 12 }}>Choose a different file</button>
                      </div>
                      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 14 }}>
                        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                          Item number column
                          <select value={importItemCol} onChange={(e) => setImportItemCol(Number(e.target.value))} style={selectStyle}>
                            {colOptions.map((i) => <option key={i} value={i}>{colLabel(i)}</option>)}
                          </select>
                        </label>
                        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                          Quantity column
                          <select value={importQtyCol} onChange={(e) => setImportQtyCol(Number(e.target.value))} style={selectStyle}>
                            {colOptions.map((i) => <option key={i} value={i}>{colLabel(i)}</option>)}
                          </select>
                        </label>
                        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: "#334155", paddingBottom: 6 }}>
                          <input type="checkbox" checked={importSkipFirstRow} onChange={(e) => setImportSkipFirstRow(e.target.checked)} /> First row is a header (skip it)
                        </label>
                      </div>
                      {sameCol && <div style={{ fontSize: 12.5, color: "#dc2626", fontWeight: 700, marginBottom: 10 }}>Item number and quantity must be different columns.</div>}
                      <div style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: 8, background: "#fff", marginBottom: 14 }}>
                        <table style={{ borderCollapse: "collapse", fontSize: 12.5, width: "100%" }}>
                          <thead>
                            <tr style={{ background: "#f1f5f9" }}>
                              {colOptions.map((i) => (
                                <th key={i} style={{ padding: "6px 12px", textAlign: "left", fontWeight: 700, color: i === importItemCol ? "#2563eb" : i === importQtyCol ? "#0891b2" : "#94a3b8", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>
                                  {colLabel(i)}{i === importItemCol ? " · item" : i === importQtyCol ? " · qty" : ""}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {previewBody.map((r, ri) => (
                              <tr key={ri} style={{ borderBottom: "1px solid #f1f5f9" }}>
                                {colOptions.map((i) => (
                                  <td key={i} style={{ padding: "5px 12px", color: i === importItemCol || i === importQtyCol ? "#0f172a" : "#94a3b8", fontWeight: i === importItemCol || i === importQtyCol ? 600 : 400, whiteSpace: "nowrap" }}>
                                    {String(r[i] ?? "")}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", alignItems: "center" }}>
                        <span style={{ fontSize: 12, color: "#64748b", marginRight: "auto" }}>Previewing first {previewBody.length} data row(s).</span>
                        <button type="button" onClick={cancelExcelImport} style={btnOutline()}>Cancel</button>
                        <button type="button" onClick={applyExcelImport} disabled={sameCol} style={btn(sameCol ? "#94a3b8" : "#0891b2", sameCol ? { cursor: "not-allowed" } : {})}>Apply Import</button>
                      </div>
                    </div>
                  );
                })()}
                {excelImportMessage && <div style={{ padding: "8px 12px", borderRadius: 8, background: "#ecfdf5", color: "#047857", border: "1px solid #a7f3d0", fontWeight: 700, fontSize: 13, marginBottom: 10 }}>{excelImportMessage}</div>}
                {excelImportError && <div style={{ padding: "8px 12px", borderRadius: 8, background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca", fontWeight: 700, fontSize: 13, marginBottom: 10 }}>{excelImportError}</div>}
                <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden" }}>
                  {projectSoqItems.length === 0 ? (
                    <div style={{ textAlign: "center", color: "#6b7280", padding: 16 }}>No SOQ items available for this project yet.</div>
                  ) : filteredValuationRows.length === 0 ? (
                    <div style={{ textAlign: "center", color: "#6b7280", padding: 16 }}>No SOQ items match your search.</div>
                  ) : (
                    <div style={{ overflowX: "auto", maxHeight: 340, overflowY: "auto" }}>
                      <table style={{ width: "100%", minWidth: 700, borderCollapse: "collapse", tableLayout: "fixed" }}>
                        <colgroup><col style={{ width: 36 }} /><col style={{ width: "30%" }} /><col style={{ width: 60 }} /><col style={{ width: 90 }} /><col style={{ width: 100 }} /><col style={{ width: 90 }} /><col style={{ width: 110 }} /><col style={{ width: 110 }} /><col style={{ width: 64 }} /></colgroup>
                        <thead>
                          <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e5e7eb" }}>
                            {["", "Item / Description", "Unit", "Prev Qty", "This Month Qty", "Cum Qty", "This Month Amt", "Amt to Date"].map((h, i) => (
                              <th key={i} style={{ padding: "8px 10px", fontWeight: 700, fontSize: 11, color: i === 4 ? "#0891b2" : "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", textAlign: i === 0 ? "center" : i === 1 ? "left" : "right", position: "sticky", top: 0, background: "#f8fafc", zIndex: 2 }}>{h}</th>
                            ))}
                            <th title="Subject to CPA — include this item in the escalatable base (on by default)" style={{ padding: "8px 6px", fontWeight: 700, fontSize: 11, color: "#0891b2", textTransform: "uppercase", letterSpacing: "0.05em", textAlign: "center", position: "sticky", top: 0, background: "#f8fafc", zIndex: 2 }}>CPA</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredValuationRows.map((row, idx) => {
                            const selectedItem = (safeCertificateForm.claimedItems || []).find((ci) => normalizeItemNo(ci.itemNo) === normalizeItemNo(row.itemNo));
                            const isSelected = Boolean(selectedItem);
                            return (
                              <tr key={row.itemId} style={{ background: isSelected ? "#f0fdfa" : idx % 2 === 0 ? "#fff" : "#fafafa", borderBottom: "1px solid #f1f5f9", cursor: "pointer" }} onClick={() => handleToggleClaimItem(row, !isSelected)}>
                                <td style={{ padding: 10, textAlign: "center" }} onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={isSelected} onChange={(e) => handleToggleClaimItem(row, e.target.checked)} /></td>
                                <td style={{ padding: 10, maxWidth: 220 }}><div style={{ fontWeight: 600, fontSize: 13, whiteSpace: "normal", lineHeight: 1.4 }}>{row.itemNo} — {row.description || "No description"}</div><div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>Rate: {currency(row.rate)} · Est. Qty: {fmt2(row.finalEstimatedQty || row.tenderQty)}</div></td>
                                <td style={{ padding: 10, textAlign: "center", fontSize: 12 }}>{row.unit || "—"}</td>
                                <td style={{ padding: 10, textAlign: "right", fontSize: 13 }}>{fmt2(selectedItem?.previousQty ?? row.previousQty)}</td>
                                <td style={{ padding: "6px 10px" }} onClick={(e) => e.stopPropagation()}><input type="number" placeholder="—" value={selectedItem?.thisMonthQty ?? ""} disabled={!isSelected} onChange={(e) => handleClaimQtyChange(row, e.target.value)} style={{ width: "100%", textAlign: "right", background: isSelected ? "#fff" : "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 6, padding: "5px 8px", fontSize: 13 }} /></td>
                                <td style={{ padding: 10, textAlign: "right", fontSize: 13 }}>{fmt2(selectedItem?.cumulativeQty ?? row.previousQty)}</td>
                                <td style={{ padding: 10, textAlign: "right", fontSize: 13, fontWeight: isSelected ? 600 : 400, color: isSelected ? "#0f172a" : "#94a3b8" }}>{currency(selectedItem?.amountThisMonth ?? 0)}</td>
                                <td style={{ padding: 10, textAlign: "right", fontSize: 13 }}>{currency(selectedItem?.amountToDate ?? numberOrZero(row.previousQty) * numberOrZero(row.rate))}</td>
                                <td style={{ padding: 10, textAlign: "center" }} onClick={(e) => e.stopPropagation()} title={isSelected ? "Subject to CPA (escalatable)" : "Add the item first"}>
                                  <input type="checkbox" checked={isSelected ? (selectedItem?.subjectToCpa !== false) : false} disabled={!isSelected} onChange={(e) => handleToggleClaimCpa(row, e.target.checked)} />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr style={{ background: "#f8fafc", borderTop: "2px solid #e5e7eb", fontWeight: 700 }}>
                            <td colSpan={6} style={{ padding: 10, textAlign: "right", fontSize: 12, color: "#64748b", textTransform: "uppercase" }}>Total This Certificate</td>
                            <td style={{ padding: 10, textAlign: "right", fontSize: 13 }}>{currency(scheduledWorksThisCertificate)}</td>
                            <td style={{ padding: 10, textAlign: "right", fontSize: 13 }}>{currency(valuationToDateTotal)}</td>
                            <td style={{ padding: 10, textAlign: "center", fontSize: 10, color: "#0891b2", fontWeight: 700 }} title="Value of this certificate's items subject to CPA">{currency(buildCpaSubjectSubtotal(safeCertificateForm.claimedItems || []))}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </div>
                {(safeCertificateForm.claimedItems || []).length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8, color: "#0891b2" }}>Selected Items Summary</div>
                    <div style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: 10, boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                      <table style={{ ...proTableStyle, minWidth: 720 }}>
                        <thead>
                          <tr>
                            <th style={proTh({ textAlign: "left", width: 70 })}>Item No</th>
                            <th style={proTh({ textAlign: "left", minWidth: 160, whiteSpace: "normal" })}>Description</th>
                            <th style={proTh({ textAlign: "center", width: 55 })}>Unit</th>
                            <th style={proTh({ textAlign: "right", width: 90 })}>Prev Qty</th>
                            <th style={proTh({ textAlign: "right", width: 110 })}>This Month Qty</th>
                            <th style={proTh({ textAlign: "right", width: 90 })}>Cum Qty</th>
                            <th style={proTh({ textAlign: "right", width: 120 })}>Rate</th>
                            <th style={proTh({ textAlign: "right", width: 130 })}>This Month Amt</th>
                            <th style={proTh({ textAlign: "right", width: 130, borderRight: "none" })}>Amt to Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(safeCertificateForm.claimedItems || []).map((item, idx) => (
                            <tr key={item.itemNo} style={{ background: idx % 2 === 0 ? "#fff" : "#f8fafc" }}>
                              <td style={proTd({ fontWeight: 600 })}>{item.itemNo}</td>
                              <td style={proTd({ whiteSpace: "normal", maxWidth: 240, fontSize: 12, lineHeight: 1.4 })}>{item.description || "-"}</td>
                              <td style={proTd({ textAlign: "center", color: "#6b7280" })}>{item.unit || "-"}</td>
                              <td style={proTd({ textAlign: "right" })}>{fmt2(item.previousQty)}</td>
                              <td style={proTd({ textAlign: "right", fontWeight: 600, color: "#0891b2" })}>{fmt2(item.thisMonthQty)}</td>
                              <td style={proTd({ textAlign: "right" })}>{fmt2(item.cumulativeQty)}</td>
                              <td style={proTd({ textAlign: "right" })}>{currency(item.rate)}</td>
                              <td style={proTd({ textAlign: "right", fontWeight: 600 })}>{currency(item.amountThisMonth)}</td>
                              <td style={proTd({ textAlign: "right", borderRight: "none" })}>{currency(item.amountToDate)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td colSpan={7} style={proTfoot({ textAlign: "right", color: "#6b7280", fontSize: 11, textTransform: "uppercase" })}>Total This Certificate</td>
                            <td style={proTfoot({ textAlign: "right" })}>{currency(scheduledWorksThisCertificate)}</td>
                            <td style={proTfoot({ borderRight: "none" })} />
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Approved VOs expanded */}
            {editSectionOpen === "vos" && (              <div style={{ border: "2px solid #7c3aed", borderRadius: 12, padding: 14, marginBottom: 14 }}>
                <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden" }}>
                  {approvedVariationRows.length === 0 ? (
                    <div style={{ textAlign: "center", color: "#6b7280", padding: 16 }}>No approved VOs available for this project yet.</div>
                  ) : (
                    <div style={{ overflowX: "auto", maxHeight: 320, overflowY: "auto" }}>
                      <table style={{ width: "100%", minWidth: 800, borderCollapse: "collapse", tableLayout: "fixed" }}>
                        <colgroup><col style={{ width: 36 }} /><col style={{ width: "28%" }} /><col style={{ width: 120 }} /><col style={{ width: 90 }} /><col style={{ width: 100 }} /><col style={{ width: 90 }} /><col style={{ width: 90 }} /><col style={{ width: 110 }} /><col style={{ width: 100 }} /></colgroup>
                        <thead>
                          <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e5e7eb" }}>
                            {["", "VO / Description", "Approved Value", "Approved Qty", "Prev Certified", "Rate", "Qty This Cert", "Amt This Cert", "Balance"].map((h, i) => (
                              <th key={i} style={{ padding: "8px 10px", fontWeight: 700, fontSize: 11, color: i === 6 ? "#7c3aed" : "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", textAlign: i === 0 ? "center" : i === 1 ? "left" : "right", position: "sticky", top: 0, background: "#f8fafc", zIndex: 2 }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {approvedVariationRows.map((row, idx) => {
                            const selectedItem = validExtraWorkItems.find((i) => i.variationId === row.variationId);
                            const isSelected = Boolean(selectedItem);
                            const displayRate = numberOrZero(selectedItem?.rate ?? row.rate);
                            const displayApprovedValue = numberOrZero(selectedItem?.approvedValue ?? row.approvedValue);
                            const displayPreviousQty = numberOrZero(selectedItem?.previouslyCertifiedQty ?? row.previouslyCertifiedQty);
                            const displayBalanceValue = selectedItem?.balanceValue !== undefined ? numberOrZero(selectedItem.balanceValue) : (displayApprovedValue - displayPreviousQty * displayRate);
                            return (
                              <tr key={row.variationId} style={{ background: isSelected ? "#faf5ff" : idx % 2 === 0 ? "#fff" : "#fafafa", borderBottom: "1px solid #f1f5f9", cursor: "pointer" }} onClick={() => handleToggleExtraWorkItem(row, !isSelected)}>
                                <td style={{ padding: 10, textAlign: "center" }} onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={isSelected} onChange={(e) => handleToggleExtraWorkItem(row, e.target.checked)} /></td>
                                <td style={{ padding: 10, maxWidth: 200 }}><div style={{ fontWeight: 600, fontSize: 13, whiteSpace: "normal", lineHeight: 1.4 }}>{row.variationNo || "VO"} — {row.title || row.description || "Approved VO"}</div></td>
                                <td style={{ padding: 10, textAlign: "right", fontSize: 13 }}>{currency(displayApprovedValue)}</td>
                                <td style={{ padding: 10, textAlign: "right", fontSize: 13 }}>{fmt2(row.approvedQty)}</td>
                                <td style={{ padding: 10, textAlign: "right", fontSize: 13 }}>{fmt2(displayPreviousQty)}</td>
                                <td style={{ padding: 10, textAlign: "right", fontSize: 13 }}>{currency(displayRate)}</td>
                                <td style={{ padding: "6px 10px" }} onClick={(e) => e.stopPropagation()}><input type="number" placeholder="—" value={selectedItem?.quantityThisCertificate ?? ""} disabled={!isSelected} onChange={(e) => handleExtraWorkQtyChange(row, e.target.value)} style={{ width: "100%", textAlign: "right", background: isSelected ? "#fff" : "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 6, padding: "5px 8px", fontSize: 13 }} /></td>
                                <td style={{ padding: 10, textAlign: "right", fontSize: 13, fontWeight: isSelected ? 600 : 400, color: isSelected ? "#0f172a" : "#94a3b8" }}>{currency(selectedItem?.amountThisCertificate ?? 0)}</td>
                                <td style={{ padding: 10, textAlign: "right", fontSize: 13, color: displayBalanceValue < 0 ? "#dc2626" : "#475569" }}>{currency(displayBalanceValue)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr style={{ background: "#f8fafc", borderTop: "2px solid #e5e7eb", fontWeight: 700 }}>
                            <td colSpan={7} style={{ padding: 10, textAlign: "right", fontSize: 12, color: "#64748b", textTransform: "uppercase" }}>Total Extra Work This Certificate</td>
                            <td style={{ padding: 10, textAlign: "right", fontSize: 13 }}>{currency(extraWorkThisCertificate)}</td>
                            <td />
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </div>
                {validExtraWorkItems.length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8, color: "#7c3aed" }}>Selected VO Summary</div>
                    <div style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: 10, boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                      <table style={{ ...proTableStyle, minWidth: 760 }}>
                        <thead>
                          <tr>
                            <th style={proTh({ textAlign: "left", width: 60 })}>VO No</th>
                            <th style={proTh({ textAlign: "left", minWidth: 140, whiteSpace: "normal" })}>Title</th>
                            <th style={proTh({ textAlign: "right", width: 90 })}>Approved Qty</th>
                            <th style={proTh({ textAlign: "right", width: 80 })}>Prev Qty</th>
                            <th style={proTh({ textAlign: "right", width: 100 })}>This Cert Qty</th>
                            <th style={proTh({ textAlign: "right", width: 80 })}>Cum Qty</th>
                            <th style={proTh({ textAlign: "right", width: 120 })}>Rate</th>
                            <th style={proTh({ textAlign: "right", width: 130 })}>Amt This Cert</th>
                            <th style={proTh({ textAlign: "right", width: 120, borderRight: "none" })}>Balance</th>
                          </tr>
                        </thead>
                        <tbody>
                          {validExtraWorkItems.map((item, idx) => (
                            <tr key={item.variationId} style={{ background: idx % 2 === 0 ? "#fff" : "#f8fafc" }}>
                              <td style={proTd({ fontWeight: 600 })}>{item.variationNo || "-"}</td>
                              <td style={proTd({ whiteSpace: "normal", maxWidth: 200, fontSize: 12, lineHeight: 1.4 })}>{item.title || item.description || "-"}</td>
                              <td style={proTd({ textAlign: "right" })}>{fmt2(item.approvedQty)}</td>
                              <td style={proTd({ textAlign: "right" })}>{fmt2(item.previouslyCertifiedQty)}</td>
                              <td style={proTd({ textAlign: "right", fontWeight: 600, color: "#7c3aed" })}>{fmt2(item.quantityThisCertificate)}</td>
                              <td style={proTd({ textAlign: "right" })}>{fmt2(item.cumulativeQty)}</td>
                              <td style={proTd({ textAlign: "right" })}>{currency(item.rate)}</td>
                              <td style={proTd({ textAlign: "right", fontWeight: 600 })}>{currency(item.amountThisCertificate)}</td>
                              <td style={proTd({ textAlign: "right", color: numberOrZero(item.balanceValue) < 0 ? "#dc2626" : "#0f172a", borderRight: "none" })}>{currency(item.balanceValue)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td colSpan={7} style={proTfoot({ textAlign: "right", color: "#6b7280", fontSize: 11, textTransform: "uppercase" })}>Total Extra Work This Certificate</td>
                            <td style={proTfoot({ textAlign: "right" })}>{currency(extraWorkThisCertificate)}</td>
                            <td style={proTfoot({ borderRight: "none" })} />
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Approved P&G Claims expanded */}
            {editSectionOpen === "png" && (
              <div style={{ border: "2px solid #b45309", borderRadius: 12, padding: 14, marginBottom: 14 }}>
                {approvedPngItems.length === 0 ? (
                  <div style={{ textAlign: "center", color: "#6b7280", padding: 16 }}>
                    No approved P&G claims with a day rate found for this project.<br />
                    <span style={{ fontSize: 12 }}>Add claims with a P&G Rate Per Day in the Claims section to track them here.</span>
                  </div>
                ) : (
                  <>
                    <div style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: 10, boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                      <table style={{ ...proTableStyle, minWidth: 680 }}>
                        <thead>
                          <tr>
                            <th style={proTh({ textAlign: "left", width: 70 })}>Claim No</th>
                            <th style={proTh({ textAlign: "left", minWidth: 160, whiteSpace: "normal" })}>Title</th>
                            <th style={proTh({ textAlign: "right", width: 110 })}>P&G Rate/Day</th>
                            <th style={proTh({ textAlign: "right", width: 90 })}>Days Awarded</th>
                            <th style={proTh({ textAlign: "right", width: 130 })}>Total Authorised</th>
                            <th style={proTh({ textAlign: "right", width: 110, color: "#b45309" })}>Days This Cert</th>
                            <th style={proTh({ textAlign: "right", width: 130, borderRight: "none" })}>Cert Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {approvedPngItems.map((item, idx) => (
                            <tr key={item.claimId} style={{ background: idx % 2 === 0 ? "#fff" : "#fdf8f0" }}>
                              <td style={proTd({ fontWeight: 600, color: "#92400e" })}>{item.claimNo || "-"}</td>
                              <td style={proTd({ whiteSpace: "normal", maxWidth: 200, fontSize: 12, lineHeight: 1.4 })}>{item.title || "-"}</td>
                              <td style={proTd({ textAlign: "right" })}>{currency(item.rate)}</td>
                              <td style={proTd({ textAlign: "right" })}>{item.daysAwarded || "—"}</td>
                              <td style={proTd({ textAlign: "right", fontWeight: 600, color: "#92400e" })}>{currency(item.totalAuthorised)}</td>
                              <td style={proTd({ textAlign: "right" })} onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="number"
                                  min="0"
                                  step="1"
                                  placeholder="0"
                                  value={item.certifiedDays !== 0 ? (item.certifiedDays || "") : ""}
                                  onChange={(e) => handlePngDaysChange(item, e.target.value)}
                                  style={{ width: "100%", textAlign: "right", border: "1px solid #e2e8f0", borderRadius: 6, padding: "5px 8px", fontSize: 13, background: "#fffbeb", fontWeight: 600 }}
                                />
                              </td>
                              <td style={proTd({ textAlign: "right", fontWeight: 600, borderRight: "none", color: item.certifiedAmt > 0 ? "#0f172a" : "#94a3b8" })}>{currency(item.certifiedAmt)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td colSpan={4} style={proTfoot({ textAlign: "right", color: "#6b7280", fontSize: 11, textTransform: "uppercase" })}>Total Approved P&G</td>
                            <td style={proTfoot({ textAlign: "right", color: "#92400e" })}>{currency(approvedPngTotal)}</td>
                            <td style={proTfoot({})} />
                            <td style={proTfoot({ textAlign: "right", borderRight: "none" })}>{currency(approvedPngCertifiedAmt)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                    <div style={{ marginTop: 10, padding: "8px 12px", background: "#fdf8f0", borderRadius: 8, border: "1px solid #fde68a", fontSize: 12, color: "#92400e", fontWeight: 600 }}>
                      ℹ Total approved P&G authorised for this project: {currency(approvedPngTotal)} · Certified this certificate: {currency(approvedPngCertifiedAmt)}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

        </CertificateModal>
      )}

      {historyRow && (
        <VersionHistory
          projectId={currentProjectId}
          collection="certificates"
          docId={historyRow.id}
          label={`Certificate ${historyRow.certificateNo || historyRow.id}`}
          onClose={() => setHistoryRow(null)}
        />
      )}

    </section>
  );
}
export { getEffectiveRetention, hasManualRetentionOverride, syncSelectedVariationItems };
export default React.memo(CertificatesSection);
